/**
 * Verona Tempo — storefront features (2026 revamp, rounds 3–8). Vanilla JS, no dependencies.
 *  - Round 8: footer accordions (phones) + back to top + footer name, sections fade in on first view,
 *    count-up numbers, marquee pause button, hero video pause button (no video on 2G)
 *  - Round 7: colour name on the photo, "In stock"/"Sold out" line, "Similar price" row, colour-of-the-season
 *    products, "Continue shopping" chip, "Your bag is waiting", undo after removing from the bag,
 *    back to the same place on a collection (with its "Load more" pages), sold-out sizes crossed out in the
 *    card sizes line
 *  - Round 6: prefetch on hover/touch (fallback for browsers without speculation rules), friendly
 *    add-to-bag errors, sticky bar colour + size, phone grid switch, load more, "Show my size" chip,
 *    compare shoes, back to top, wishlist sharing, size converter, skeletons, colour-matched outfits,
 *    tap equivalents for hover-only card details (sizes line, "+N more" colours, colour names)
 *  - Round 5: "Save $X" line, sizes on product cards, bag/heart micro-animations, free delivery
 *    hint on product pages, size memory, change size/colour in the bag, "Watch it worn" video,
 *    "Shop similar" when sold out, search normalisation (Arabic words + typos), share button
 *  - Round 4: free-delivery bar, outfit cards ("add all to bag"), empty-cart best sellers,
 *    size guide popup, product gallery counter
 *  - Mobile bottom navigation (menu / search / cart drawer / counts)
 *  - Wishlist (localStorage "vt-wishlist") + wishlist page grid
 *  - Recently viewed (localStorage "vt-recently-viewed", max 12)
 *  - "Only X left" badges on variant buttons
 *  - Verona Rewards points on product pages
 *  - "Complete your outfit" in the cart drawer
 *  - WhatsApp request forms (bridal appointment, track order) and copy-link button
 * Config + translated strings: window.vtConfig (snippets/vt-config.liquid).
 *
 * Round 9 (T3) — page files. This file runs on every page. Features only needed on one kind of page live in:
 *  - assets/vt-product.js (product pages): recently viewed record, gallery counter, free delivery hint,
 *    size memory, "Watch it worn" video, "Shop similar", sticky bar colour + size, "Similar price" row
 *  - assets/vt-collection.js (collection + search): phone grid switch, "Load more", "Show my size" chip,
 *    back to the same place on a collection
 * Both are loaded (deferred) after this file by layout/theme.liquid and use the shared helpers below.
 * Anything that also runs in the quick view (collection / home / search), the cart, header, footer, cards or
 * other pages stays here.
 *
 * window.vtCore — shared helpers (set before the page files run):
 *   cfg, S                 window.vtConfig and its translated strings (never hard-code English in JS)
 *   keys                   storage keys: wishlist, recent (+ recentMax), sizeMemory
 *   root(), route(path)    shop root ("/" or "/ar/") and a localised url: route('cart.js')
 *   tpl(str, vars)         "[count] left" style placeholders;  esc(str) HTML-escape
 *   formatMoney(cents)     store money format;  shortMoney(cents) "$4" without ".00"
 *   readList/writeList(key[, list])   string arrays in localStorage;  storageGet/storageSet(store, key[, value])
 *   readSizeMemory() / writeSizeMemory(cat, size)   her remembered sizes { shoes: "38" }
 *   onReady(fn)            run after the DOM is parsed;  parseJsonEl(el, fallback);  jsonFetch(url, body) POST JSON
 *   loadScriptOnce(src[, force])   third-party script, once (Promise)
 *   reducedMotion(), animationsOn(), belowScreen(el), scrollToTop()
 *   isSizeOption(name), colourOption(productEl), imageUrl(src, width), fetchProduct(handle) (cached /products/x.js)
 *   skeletonCards(n), cardHtml(product, opts), normaliseCollectionProduct(p)
 *   onCart(fn(cart)), refreshCart(), openCartAfterAdd(opener, sections), friendlyError(msg)
 *   announce(msg)          polite screen-reader message
 *   onListingRefresh(fn)   called after Symmetry re-renders the collection / search list (filters, sort)
 *   normaliseSearch(q), wishlist ({ get, has, toggle, ... } — same as window.vtWishlist)
 * Older globals kept: window.vtWishlist, vtDrawCardLists, vtNormaliseSearch, vtSyncCompare, vtTheme.
 */
(function () {
  'use strict';

  var cfg = window.vtConfig || {};
  var S = cfg.strings || {};
  var WISHLIST_KEY = 'vt-wishlist';
  var RECENT_KEY = 'vt-recently-viewed';
  var RECENT_MAX = 12;

  /* ---------------------------------------------------------------- utils */
  function root() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }
  function route(path) {
    return root() + String(path).replace(/^\//, '');
  }
  function tpl(str, vars) {
    return String(str || '').replace(/\[(\w+)\]/g, function (m, k) {
      return vars && vars[k] !== undefined ? vars[k] : m;
    });
  }
  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function readList(key) {
    try {
      var v = JSON.parse(window.localStorage.getItem(key) || '[]');
      return Array.isArray(v) ? v.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (e) {
      return [];
    }
  }
  function writeList(key, list) {
    try { window.localStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* storage blocked */ }
  }
  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }
  function formatMoney(cents) {
    var format = String(cfg.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    var value = (typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents) || 0;
    function withDelimiters(num, precision, thousands, decimal) {
      var fixed = (num / 100).toFixed(precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var out;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': out = withDelimiters(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': out = withDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = withDelimiters(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = withDelimiters(value, 2, "'", '.'); break;
      default: out = withDelimiters(value, 2, ',', '.');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, out);
  }
  /* r5: size option detection (Theme settings > Verona Tempo > Size option names) */
  var SIZE_NAMES = (cfg.sizeNames && cfg.sizeNames.length ? cfg.sizeNames : ['size', 'shoe size', 'المقاس', 'مقاس'])
    .map(function (n) { return String(n).trim().toLowerCase(); }).filter(Boolean);
  function isSizeOption(name) {
    return SIZE_NAMES.indexOf(String(name || '').trim().toLowerCase()) > -1;
  }
  function imageUrl(src, width) {
    if (!src) return '';
    var url = src.indexOf('//') === 0 ? 'https:' + src : src;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  var productCache = {};
  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch(route('products/' + encodeURIComponent(handle) + '.js'), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  /* r6 (D6): grey placeholder cards while lists load */
  function skeletonCards(n) {
    var html = '';
    for (var i = 0; i < n; i += 1) {
      html += '<li class="vt-card vt-skel" aria-hidden="true"><span class="vt-card__media vt-skel__box"></span>' +
        '<span class="vt-skel__line"></span><span class="vt-skel__line vt-skel__line--short"></span></li>';
    }
    return html;
  }

  function cardHtml(p, opts) {
    opts = opts || {};
    var url = route('products/' + p.handle);
    var img = p.featured_image;
    var media = img
      ? '<img src="' + esc(imageUrl(img, 400)) + '" srcset="' + esc(imageUrl(img, 240)) + ' 240w, ' + esc(imageUrl(img, 400)) + ' 400w, ' + esc(imageUrl(img, 600)) + ' 600w" sizes="(min-width: 1024px) 16vw, (min-width: 768px) 25vw, 50vw" alt="' + esc(p.title) + '" loading="lazy" width="600" height="600">'
      : '';
    var price = '<span class="vt-card__price-now">' + esc(formatMoney(p.price)) + '</span>';
    if (p.compare_at_price && p.compare_at_price > p.price) {
      price = '<s class="vt-card__price-was">' + esc(formatMoney(p.compare_at_price)) + '</s> ' + price.replace('vt-card__price-now', 'vt-card__price-now vt-card__price-now--sale');
    }
    var html = '<li class="vt-card" data-handle="' + esc(p.handle) + '">' +
      '<a class="vt-card__link" href="' + esc(url) + '">' +
      '<span class="vt-card__media">' + media + (p.available ? '' : '<span class="vt-card__badge">' + esc(S.soldOut) + '</span>') + '</span>' +
      '<span class="vt-card__title">' + esc(p.title) + '</span>' +
      '<span class="vt-card__price">' + price + '</span>' +
      '</a>';
    if (opts.removable) {
      html += '<button type="button" class="vt-card__remove" data-vt-remove="' + esc(p.handle) + '" aria-label="' + esc(tpl(S.remove, { title: p.title })) + '">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg></button>';
    }
    return html + '</li>';
  }

  /* ------------------------------------------------------------- wishlist */
  var wishlist = {
    get: function () { return readList(WISHLIST_KEY); },
    has: function (h) { return wishlist.get().indexOf(h) > -1; },
    set: function (list) {
      writeList(WISHLIST_KEY, list);
      document.dispatchEvent(new CustomEvent('vt:wishlist:change', { detail: { handles: list } }));
    },
    toggle: function (h) {
      var list = wishlist.get();
      var i = list.indexOf(h);
      if (i > -1) list.splice(i, 1); else list.unshift(h);
      wishlist.set(list);
      return i === -1;
    },
    remove: function (h) {
      wishlist.set(wishlist.get().filter(function (x) { return x !== h; }));
    }
  };
  window.vtWishlist = wishlist;

  function syncWishlistUI() {
    var list = wishlist.get();
    document.querySelectorAll('[data-vt-wishlist-toggle]').forEach(function (btn) {
      var on = list.indexOf(btn.dataset.handle) > -1;
      var label = on ? S.wishlistRemove : S.wishlistAdd;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-label', label + (btn.dataset.title ? ': ' + btn.dataset.title : ''));
      btn.title = label;
      var text = btn.querySelector('[data-vt-wishlist-text]');
      if (text && text.textContent !== label) text.textContent = label;
    });
    var count = list.length;
    document.querySelectorAll('[data-vt-wishlist-count]').forEach(function (el) {
      var val = count > 0 ? String(count > 99 ? '99+' : count) : '';
      if (el.textContent !== val) el.textContent = val;
    });
  }

  function initWishlist() {
    if (!cfg.wishlist) return;
    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('[data-vt-wishlist-toggle]');
      if (!btn) return;
      evt.preventDefault();
      evt.stopPropagation();
      var added = wishlist.toggle(btn.dataset.handle);
      btn.classList.remove('vt-wish--pop');
      // restart the small "pop" animation
      void btn.offsetWidth;
      if (added) btn.classList.add('vt-wish--pop');
      announce(added ? S.wishlistAdded : S.wishlistRemoved);
    }, true);
    document.addEventListener('vt:wishlist:change', syncWishlistUI);
    window.addEventListener('storage', function (e) { if (e.key === WISHLIST_KEY) syncWishlistUI(); });
    // Symmetry re-renders product grids (filters, infinite load, quick buy, cart header) — keep hearts in sync.
    var pending = false;
    new MutationObserver(function (mutations) {
      if (pending) return;
      for (var i = 0; i < mutations.length; i += 1) {
        if (mutations[i].addedNodes.length) {
          pending = true;
          window.requestAnimationFrame(function () { pending = false; syncWishlistUI(); if (window.vtSyncCompare) window.vtSyncCompare(); });
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
    syncWishlistUI();
  }

  var liveRegion = null;
  function announce(msg) {
    if (!msg) return;
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    window.setTimeout(function () { liveRegion.textContent = msg; }, 50);
  }

  /* Wishlist page grid */
  if (!customElements.get('vt-wishlist-grid')) {
    customElements.define('vt-wishlist-grid', class extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-vt-list]');
        this.empty = this.querySelector('[data-vt-empty]');
        this.loading = this.querySelector('[data-vt-loading]');
        this.status = this.querySelector('[data-vt-status]');
        // r6 (U3): a shared list (?items=handle1,handle2) is shown read-only with "Save to my wishlist"
        this.shared = this.sharedHandles();
        this.addEventListener('click', (evt) => {
          var shareBtn = evt.target.closest('[data-vt-wl-share]');
          if (shareBtn) { evt.preventDefault(); this.share(shareBtn); return; }
          var saveBtn = evt.target.closest('[data-vt-wl-save]');
          if (saveBtn) { evt.preventDefault(); this.saveShared(saveBtn); return; }
          var btn = evt.target.closest('[data-vt-remove]');
          if (!btn) return;
          evt.preventDefault();
          var handle = btn.dataset.vtRemove;
          var item = btn.closest('.vt-card');
          var title = item ? (item.querySelector('.vt-card__title') || {}).textContent : '';
          // r10: focus moves to the next card (or the previous one / the empty-state button) — never lost
          var next = item ? (item.nextElementSibling || item.previousElementSibling) : null;
          wishlist.remove(handle);
          if (item) item.remove();
          if (this.status) this.status.textContent = S.wishlistRemoved + (title ? ': ' + title : '');
          this.toggleEmpty();
          var target = next && next.isConnected ? next.querySelector('.vt-card__link:not([tabindex="-1"]), [data-vt-remove]') : null;
          if (!target && this.empty && !this.empty.hidden) target = this.empty.querySelector('a, button');
          if (target) target.focus();
        });
        this.render();
      }

      toggleEmpty() {
        var hasItems = this.list && this.list.children.length > 0;
        if (this.list) this.list.hidden = !hasItems;
        if (this.empty) this.empty.hidden = hasItems;
        var shareBox = this.querySelector('[data-vt-wl-share-box]');
        if (shareBox) shareBox.hidden = !hasItems || !!this.shared;
        // r10 (C): count under the heading + "Add all to bag" (own list only)
        var n = this.list ? this.list.children.length : 0;
        var box = this.closest('.vt-wishlist');
        var countEl = box && box.querySelector('[data-vt-wl-count]');
        if (countEl) {
          countEl.textContent = n === 1 ? countEl.dataset.one : tpl(countEl.dataset.other, { count: n });
          countEl.hidden = !n;
        }
        var addAll = this.querySelector('[data-vt-wl-addall-box]');
        if (addAll) addAll.hidden = !hasItems || !!this.shared;
      }

      /* r10 (A/F): the same clean card as the product lists — photo (same shape), one label (Sold out > Sale),
         name, price (sale: new price + struck old price), up to 4 colour dots + "+N", heart = remove */
      card10(p, removable, W) {
        var url = route('products/' + p.handle);
        var img = p.featured_image;
        var sale = p.compare_at_price && p.compare_at_price > p.price;
        var label = !p.available ? '<span class="vt10-label vt10-label--sold"><span>' + esc(W.soldOutLabel || S.soldOut) + '</span></span>'
          : (sale ? '<span class="vt10-label vt10-label--sale"><span>' + esc(W.saleLabel || '') + '</span></span>' : '');
        var media = img
          ? '<img src="' + esc(imageUrl(img, 480)) + '" srcset="' + esc(imageUrl(img, 300)) + ' 300w, ' + esc(imageUrl(img, 480)) + ' 480w, ' + esc(imageUrl(img, 720)) + ' 720w" sizes="(min-width: 1024px) 22vw, (min-width: 768px) 30vw, 46vw" alt="" loading="lazy" width="720" height="720">'
          : '';
        var price = sale
          ? '<span class="visually-hidden">' + esc(W.salePrice || '') + '</span><span class="vt10-price__now vt10-price__now--sale">' + esc(formatMoney(p.price)) + '</span> ' +
            '<span class="visually-hidden">' + esc(W.regularPrice || '') + '</span><s class="vt10-price__was">' + esc(formatMoney(p.compare_at_price)) + '</s>'
          : '<span class="vt10-price__now">' + esc(formatMoney(p.price)) + '</span>';
        var dots = '';
        (p.options || []).some(function (o, i) {
          if (COLOUR_NAMES.indexOf(String(o.name || '').trim().toLowerCase()) === -1 && ['اللون', 'لون'].indexOf(String(o.name || '').trim()) === -1) return false;
          var values = o.values || [];
          if (values.length < 2) return true;
          dots = '<span class="vt10-dots" aria-hidden="true">' + values.slice(0, 4).map(function (v) {
            return '<span class="vt10-dot" data-swatch="' + esc(String(v).replace(/"/g, '').toLowerCase()) + '"></span>';
          }).join('') + (values.length > 4 ? '<span class="vt10-dots__more">+' + (values.length - 4) + '</span>' : '') + '</span>';
          return true;
        });
        var html = '<li class="vt-card vt10-wcard' + (p.available ? '' : ' is-sold') + '" data-handle="' + esc(p.handle) + '">' +
          '<a class="vt-card__link" href="' + esc(url) + '">' +
          '<span class="vt-card__media vt10-wcard__media">' + media + label + '</span>' +
          '<span class="vt-card__title">' + esc(p.title) + '</span>' +
          '<span class="vt10-price">' + price + '</span>' + dots +
          '</a>';
        if (removable) {
          html += '<button type="button" class="vt-card__remove vt10-wcard__heart" data-vt-remove="' + esc(p.handle) + '" aria-label="' + esc(tpl(W.remove || S.remove, { title: p.title })) + '">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.2s-7.8-4.6-7.8-10.3A4.4 4.4 0 0 1 12 7.2a4.4 4.4 0 0 1 7.8 2.7c0 5.7-7.8 10.3-7.8 10.3z"/></svg></button>';
        }
        return html + '</li>';
      }

      sharedHandles() {
        var raw = null;
        try { raw = new URLSearchParams(window.location.search).get('items'); } catch (e) { raw = null; }
        if (!raw) return null;
        var list = raw.split(',').map(function (h) { return h.trim().toLowerCase(); })
          .filter(function (h) { return /^[a-z0-9][a-z0-9\-_]{0,120}$/.test(h); });
        list = list.filter(function (h, i) { return list.indexOf(h) === i; }).slice(0, 48);
        return list.length ? list : null;
      }

      share(btn) {
        var handles = wishlist.get().slice(0, 30);
        if (!handles.length) return;
        var url = window.location.origin + route('pages/wishlist') + '?items=' + handles.map(encodeURIComponent).join(',');
        var status = this.querySelector('[data-vt-wl-share-status]');
        var done = function (msg) { if (status) status.textContent = msg; };
        if (navigator.share) {
          navigator.share({ title: btn.dataset.shareTitle || document.title, url: url }).catch(function () {});
          return;
        }
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(url).then(function () { done(S.wishlistLinkCopied); }, function () { window.prompt(S.wishlistShare, url); });
        } else {
          window.prompt(S.wishlistShare, url);
        }
      }

      saveShared(btn) {
        var mine = wishlist.get();
        (this.shared || []).slice().reverse().forEach(function (h) {
          if (mine.indexOf(h) === -1) mine.unshift(h);
        });
        wishlist.set(mine);
        btn.disabled = true;
        btn.textContent = S.wishlistSaved;
        announce(S.wishlistSaved);
      }

      render() {
        var limit = parseInt(this.dataset.limit, 10) || 48;
        var shared = this.shared;
        var handles = (shared || wishlist.get()).slice(0, limit);
        var sharedBox = this.querySelector('[data-vt-wl-shared]');
        if (sharedBox) sharedBox.hidden = !shared;
        this.classList.toggle('is-shared', !!shared);
        if (!handles.length) {
          if (this.loading) this.loading.hidden = true;
          this.toggleEmpty();
          return;
        }
        // r10: clean cards when the wishlist section has the round-10 look (texts from its [data-vt-wl-strings])
        var W = this.closest('.vt-wl10') ? parseJsonEl(this.querySelector('[data-vt-wl-strings]'), {}) : null;
        Promise.all(handles.map(fetchProduct)).then((products) => {
          var missing = [];
          var html = '';
          products.forEach((p, i) => {
            if (p) html += W ? this.card10(p, !shared, W) : cardHtml(p, { removable: !shared });
            else missing.push(handles[i]);
          });
          // forget products that no longer exist on the store (own list only)
          if (missing.length && !shared) wishlist.set(wishlist.get().filter(function (h) { return missing.indexOf(h) === -1; }));
          if (this.list) this.list.innerHTML = html;
          if (this.loading) this.loading.hidden = true;
          this.toggleEmpty();
        });
      }
    });
  }

  /* -------------------------------------------------------- recently viewed */
  /* recently viewed: record this product: moved to vt-product.js (round 9 T3) */

  if (!customElements.get('vt-recently-viewed')) {
    customElements.define('vt-recently-viewed', class extends HTMLElement {
      connectedCallback() {
        if (this.rendered) return;
        this.rendered = true;
        var limit = parseInt(this.dataset.limit, 10) || 6;
        var current = cfg.productHandle;
        var handles = readList(RECENT_KEY).filter(function (h) { return h !== current; }).slice(0, limit);
        var list = this.querySelector('[data-vt-list]');
        if (!handles.length || !list) return;
        if (cfg.skeletons) {
          list.innerHTML = skeletonCards(handles.length);
          list.setAttribute('aria-busy', 'true');
          this.hidden = false;
        }
        Promise.all(handles.map(fetchProduct)).then((products) => {
          var html = products.filter(Boolean).map(function (p) { return cardHtml(p); }).join('');
          list.removeAttribute('aria-busy');
          if (!html) { list.innerHTML = ''; this.hidden = true; return; }
          list.innerHTML = html;
          this.hidden = false;
        });
      }
    });
  }

  /* ------------------------------------------------------- low stock badges */
  function lowStockData(picker) {
    if (picker.vtStock !== undefined) return picker.vtStock;
    picker.vtStock = null;
    try {
      var stockEl = picker.querySelector('script[data-vt-stock]');
      var variantsEl = picker.querySelector('script[type="application/json"]');
      if (stockEl && variantsEl) {
        var stock = JSON.parse(stockEl.textContent);
        if (Object.keys(stock).length) {
          picker.vtStock = { stock: stock, variants: JSON.parse(variantsEl.textContent).variants || [] };
        }
      }
    } catch (e) { picker.vtStock = null; }
    return picker.vtStock;
  }

  function updateLowStock(picker) {
    var data = lowStockData(picker);
    if (!data) return;
    var selectors = Array.prototype.slice.call(picker.querySelectorAll('.option-selector:not(.option-selector--custom)'));
    // r5: badges only on size buttons (never on colours); counts follow the other selected options (e.g. colour)
    var selected = selectors.map(function (sel) {
      var checked = sel.querySelector('input:checked');
      if (checked) return checked.value;
      var select = sel.querySelector('select, input[type="hidden"]');
      return select && select.value ? select.value : null;
    });
    selectors.forEach(function (sel, index) {
      var isSize = isSizeOption(sel.dataset.option);
      sel.querySelectorAll('input.js-option[type="radio"]').forEach(function (input) {
        var label = input.nextElementSibling;
        if (!label) return;
        var old = label.querySelectorAll('.vt-stock');
        old.forEach(function (el) { el.remove(); });
        label.classList.remove('vt-low');
        if (!isSize) return;
        var wanted = selected.slice();
        wanted[index] = input.value;
        if (wanted.indexOf(null) > -1) return;
        var match = null;
        for (var i = 0; i < data.variants.length; i += 1) {
          var v = data.variants[i];
          var ok = true;
          for (var j = 0; j < wanted.length; j += 1) {
            if (v.options[j] !== wanted[j]) { ok = false; break; }
          }
          if (ok) { match = v; break; }
        }
        var qty = match && data.stock[match.id];
        if (!qty || !match.available) return;
        label.classList.add('vt-low');
        // r11 (U2): "Last one" for a single pair when the luxury product page is on (variant-picker data-vt-last-one)
        var one = qty === 1 && picker.dataset.vtLastOne;
        label.insertAdjacentHTML('beforeend',
          '<span class="vt-stock" aria-hidden="true">' + esc(one ? picker.dataset.vtLastOne : tpl(S.lowStockBadge, { count: qty })) + '</span>' +
          '<span class="vt-stock visually-hidden">' + esc(one ? (picker.dataset.vtLastOneFull || picker.dataset.vtLastOne) : tpl(S.lowStockFull, { count: qty })) + '</span>');
      });
    });
  }

  function initLowStock() {
    if (!cfg.lowStock) return;
    var run = function (scope) {
      (scope || document).querySelectorAll('variant-picker').forEach(updateLowStock);
    };
    run();
    document.addEventListener('change', function (evt) {
      var picker = evt.target.closest && evt.target.closest('variant-picker');
      if (picker) window.setTimeout(function () { updateLowStock(picker); }, 0);
    });
    document.addEventListener('on:quickbuy:after-open', function (evt) { run(evt.target); });
  }

  /* ---------------------------------------------------------- reward points */
  function initPoints() {
    document.addEventListener('on:variant:change', function (evt) {
      var scope = evt.target.closest ? evt.target.closest('.js-product') : null;
      var box = scope && scope.querySelector('[data-vt-points]');
      var v = evt.detail && evt.detail.variant;
      if (!box || !v) return;
      var points = Math.floor((v.price || 0) / 100);
      var value = box.querySelector('[data-vt-points-value]');
      if (value) value.textContent = points;
      box.hidden = points < 1;
    });
  }

  /* ------------------------------------------------------ bottom navigation */
  function initBottomNav() {
    var nav = document.querySelector('[data-vt-bnav]');
    if (!nav) return;
    nav.addEventListener('click', function (evt) {
      var item = evt.target.closest('[data-vt-bnav-action]');
      if (!item) return;
      var action = item.dataset.vtBnavAction;
      if (action === 'menu') {
        var toggle = document.querySelector('.section-header .mobile-nav-toggle');
        if (toggle) {
          evt.preventDefault();
          toggle.click();
        }
      } else if (action === 'search') {
        var searchLink = document.querySelector('.section-header .show-search-link');
        if (searchLink && document.querySelector('.section-header .main-search')) {
          evt.preventDefault();
          searchLink.click();
        }
      } else if (action === 'cart') {
        if (cfg.cartType === 'drawer' && document.querySelector('.js-cart-drawer')) {
          evt.preventDefault();
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: item } }));
        }
      }
    });
    onCart(function (cart) {
      document.querySelectorAll('[data-vt-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count > 0 ? String(cart.item_count > 99 ? '99+' : cart.item_count) : '';
      });
    });
  }

  /* -------------------------------------------- cart drawer: complete outfit */
  if (!customElements.get('vt-cart-upsell')) {
    customElements.define('vt-cart-upsell', class extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-vt-upsell-list]');
        this.status = this.querySelector('[data-vt-upsell-status]');
        this.signature = null;
        var drawer = this.closest('cart-drawer') || document;
        this.boundRefresh = () => this.refresh();
        drawer.addEventListener('on:cart-drawer:before-open', this.boundRefresh);
        drawer.addEventListener('on:cart:after-merge', this.boundRefresh);
        this.addEventListener('submit', (evt) => this.onSubmit(evt));
        this.addEventListener('change', (evt) => {
          if (evt.target.matches('select[name="id"]')) {
            var form = evt.target.closest('form');
            var btn = form && form.querySelector('button');
            if (btn) btn.disabled = !evt.target.value;
          }
        });
      }

      refresh() {
        fetch(route('cart.js'), { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then((cart) => {
            if (!cart || !cart.items.length) {
              this.hidden = true;
              this.signature = null;
              return;
            }
            var inCart = cart.items.map(function (i) { return i.product_id; });
            var signature = inCart.join(',');
            if (signature === this.signature) return;
            this.signature = signature;
            var pid = cart.items[0].product_id;
            if (cfg.skeletons && this.list && !this.list.children.length) {
              var skel = '<li class="vt-upsell__item vt-skel" aria-hidden="true"><span class="vt-upsell__media vt-skel__box"></span>' +
                '<span class="vt-upsell__info"><span class="vt-skel__line"></span><span class="vt-skel__line vt-skel__line--short"></span></span></li>';
              this.list.innerHTML = skel + skel;
              this.hidden = false;
            }
            var load = function (intent) {
              return fetch(route('recommendations/products.json?product_id=' + pid + '&limit=10&intent=' + intent))
                .then(function (r) { return r.ok ? r.json() : { products: [] }; })
                .then(function (d) {
                  // 9 Oct 2026 (ads team: "COSY5 with 1 size left, BILI 3 sizes"): only pieces with most sizes in stock,
                  // like the top of the category pages - 60%+ of all sizes, and its best colour misses at most one size
                  var wellStocked = function (p) {
                    var v = p.variants || [];
                    if (v.length <= 1) return p.available;
                    var a = v.filter(function (x) { return x.available; });
                    if (a.length < 3 || a.length * 5 < v.length * 3) return false;
                    var names = (p.options || []).map(function (o) { return String(o && o.name ? o.name : o).toLowerCase(); });
                    var ci = -1;
                    names.forEach(function (n, i) { if (/colou?r|اللون|لون/.test(n)) ci = i; });
                    if (ci < 0) return true;
                    var key = 'option' + (ci + 1);
                    var all = {}, ok = {};
                    v.forEach(function (x) { all[x[key]] = (all[x[key]] || 0) + 1; if (x.available) ok[x[key]] = (ok[x[key]] || 0) + 1; });
                    return Object.keys(all).some(function (c) { return (ok[c] || 0) >= all[c] - 1 && (ok[c] || 0) >= 3; });
                  };
                  return (d.products || []).filter(function (p) { return p.available && inCart.indexOf(p.id) === -1 && wellStocked(p); });
                })
                .catch(function () { return []; });
            };
            load('complementary').then(function (products) {
              return products.length ? products : load('related');
            }).then((products) => {
              if (signature !== this.signature) return;
              products = products.slice(0, 4);
              if (!products.length) {
                this.hidden = true;
                if (this.list) this.list.innerHTML = '';
                return;
              }
              this.list.innerHTML = products.map((p) => this.itemHtml(p)).join('');
              this.hidden = false;
            });
          })
          .catch(() => { this.hidden = true; });
      }

      itemHtml(p) {
        var available = (p.variants || []).filter(function (v) { return v.available; });
        var single = p.variants.length === 1;
        var url = route('products/' + p.handle);
        var img = p.featured_image
          ? '<img src="' + esc(imageUrl(p.featured_image, 240)) + '" alt="" loading="lazy" width="240" height="240">'
          : '';
        var uid = 'vt-up-' + p.id;
        var picker = '';
        if (single) {
          picker = '<input type="hidden" name="id" value="' + (available[0] ? available[0].id : '') + '">';
        } else {
          picker = '<label class="visually-hidden" for="' + uid + '">' + esc(S.chooseSize) + '</label>' +
            '<select id="' + uid + '" name="id" class="vt-upsell__select"><option value="">' + esc(S.chooseSize) + '</option>' +
            available.map(function (v) { return '<option value="' + v.id + '">' + esc(v.title) + '</option>'; }).join('') +
            '</select>';
        }
        return '<li class="vt-upsell__item">' +
          '<a class="vt-upsell__media" href="' + esc(url) + '" tabindex="-1" aria-hidden="true">' + img + '</a>' +
          '<div class="vt-upsell__info">' +
          '<a class="vt-upsell__title" href="' + esc(url) + '">' + esc(p.title) + '</a>' +
          '<span class="vt-upsell__price">' + esc(formatMoney(p.price)) + '</span>' +
          '<form class="vt-upsell__form" novalidate>' + picker +
          '<button type="submit" class="btn btn--secondary vt-upsell__add"' + (single ? '' : ' disabled') + ' aria-label="' + esc(S.addToBag + ': ' + p.title) + '">' + esc(S.addToBag) + '</button>' +
          '</form></div></li>';
      }

      onSubmit(evt) {
        var form = evt.target.closest('.vt-upsell__form');
        if (!form) return;
        evt.preventDefault();
        var idField = form.querySelector('[name="id"]');
        var btn = form.querySelector('button');
        var id = idField && parseInt(idField.value, 10);
        if (!id) {
          if (idField && idField.focus) idField.focus();
          return;
        }
        btn.disabled = true;
        btn.textContent = S.adding;
        fetch(route('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
        })
          .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
          .then((res) => {
            if (!res.ok) throw new Error(friendlyError((res.data && (res.data.description || res.data.message)) || S.addError));
            this.signature = null;
            if (this.status) { this.status.textContent = ''; this.status.hidden = true; }
            document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: id } }));
            document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
          })
          .catch((err) => {
            btn.disabled = false;
            btn.textContent = S.addToBag;
            if (this.status) {
              this.status.textContent = err.message || S.addError;
              this.status.hidden = false;
            }
          });
      }
    });
  }

  /* ------------------------------------------------------ cart helpers (r4) */
  var cartListeners = [];
  var cartFetchTimer = null;
  function onCart(fn) { cartListeners.push(fn); }
  function refreshCart() {
    window.clearTimeout(cartFetchTimer);
    cartFetchTimer = window.setTimeout(function () {
      fetch(route('cart.js'), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          cartListeners.forEach(function (fn) { try { fn(cart); } catch (e) { /* ignore */ } });
        })
        .catch(function () {});
    }, 250);
  }
  function openCartAfterAdd(opener, sections) {
    if (sections) {
      ['page-header', 'cart-drawer'].forEach(function (sel) {
        var el = document.querySelector(sel);
        if (el && el.dataset.sectionId && sections[el.dataset.sectionId] && el.updateFromCartChange) {
          el.updateFromCartChange(sections[el.dataset.sectionId]);
        }
      });
    }
    var drawer = document.querySelector('.js-cart-drawer');
    if (document.body.classList.contains('template-cart')) {
      window.location.reload();
    } else if (cfg.cartType === 'drawer' && drawer) {
      if (!sections) document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: opener } }));
    } else {
      window.location.href = route('cart');
    }
  }

  /* ------------------------------------------- free-delivery bar (phones) */
  var FD_KEY = 'vt-fd-dismissed';
  function shortMoney(cents) {
    return formatMoney(cents).replace(/[.,]00(?=D*$)/, '');
  }
  if (!customElements.get('vt-free-delivery')) {
    customElements.define('vt-free-delivery', class extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.threshold = parseInt(this.dataset.threshold, 10) || 0;
        this.text = this.querySelector('[data-vt-fd-text]');
        this.fill = this.querySelector('[data-vt-fd-fill]');
        var dismissed = false;
        try { dismissed = window.sessionStorage.getItem(FD_KEY) === '1'; } catch (e) { dismissed = false; }
        if (dismissed || !this.threshold) return;
        this.querySelector('[data-vt-fd-close]').addEventListener('click', () => {
          try { window.sessionStorage.setItem(FD_KEY, '1'); } catch (e) { /* storage blocked */ }
          this.hidden = true;
          document.body.classList.remove('vt-has-fd');
        });
        this.render(parseInt(this.dataset.total, 10) || 0, parseInt(this.dataset.count, 10) || 0);
        onCart((cart) => { if (!this.hidden) this.render(cart.total_price, cart.item_count); });
      }

      render(total, count) {
        var msg;
        var progress = 0;
        if (!count) {
          msg = tpl(S.fdInfo, { amount: this.dataset.label || shortMoney(this.threshold) });
        } else if (total >= this.threshold) {
          msg = S.fdUnlocked;
          progress = 1;
        } else {
          msg = tpl(S.fdAdd, { amount: shortMoney(this.threshold - total) });
          progress = total / this.threshold;
        }
        this.classList.toggle('is-unlocked', !!count && total >= this.threshold);
        this.classList.toggle('has-progress', !!count);
        if (this.text.textContent !== msg) this.text.textContent = msg;
        if (this.fill) this.fill.style.transform = 'scaleX(' + Math.max(0, Math.min(1, progress)).toFixed(3) + ')';
        this.hidden = false;
        document.body.classList.add('vt-has-fd');
      }
    });
  }

  /* ---------------------------------------------------- outfit card (r4) */
  if (!customElements.get('vt-outfit')) {
    customElements.define('vt-outfit', class extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.button = this.querySelector('[data-vt-outfit-add]');
        this.status = this.querySelector('[data-vt-outfit-status]');
        this.totalEl = this.querySelector('[data-vt-outfit-total]');
        this.buttonLabel = this.button ? this.button.textContent : '';
        this.addEventListener('change', (evt) => {
          if (evt.target.matches('[data-vt-outfit-variant], [data-vt-outfit-include]')) {
            var item = evt.target.closest('[data-vt-outfit-item]');
            if (item) item.classList.remove('has-error');
            this.setStatus('');
            this.update();
          }
        });
        if (this.button) this.button.addEventListener('click', () => this.addAll());
        if (this.dataset.context === 'pdp') {
          var product = this.closest('.js-product');
          if (product) {
            product.addEventListener('on:variant:change', (evt) => {
              var v = evt.detail && evt.detail.variant;
              var main = this.querySelector('[data-vt-outfit-main] select[data-vt-outfit-variant]');
              if (!main) return;
              var option = v ? main.querySelector('option[value="' + v.id + '"]') : null;
              if (option) main.value = String(v.id);
              if (v) this.matchColour(v);
              this.update();
            });
          }
        }
        this.update();
      }

      /* r6 (D9): prefer suggestions that come in the colour she picked; always keep `limit` items */
      matchColour(variant) {
        if (!this.hasAttribute('data-colour-match')) return;
        var ci = parseInt(this.dataset.colourIndex, 10);
        if (isNaN(ci) || ci < 0 || !variant.options) return;
        var colour = String(variant.options[ci] || '').toLowerCase().trim();
        if (!colour || colour === this.lastColour) return;
        this.lastColour = colour;
        var list = this.querySelector('.vt-outfit__items');
        var items = Array.prototype.slice.call(this.querySelectorAll('[data-vt-outfit-item]:not([data-vt-outfit-main])'));
        if (!list || items.length < 2) return;
        items.forEach(function (el, i) { if (el.dataset.order === undefined) el.dataset.order = i; });
        var limit = parseInt(this.dataset.limit, 10) || 2;
        var score = function (el) {
          return String(el.dataset.colours || '').split('|').some(function (c) {
            c = c.trim();
            return c && (c === colour || c.indexOf(colour) > -1 || colour.indexOf(c) > -1);
          }) ? 1 : 0;
        };
        var sorted = items.slice().sort(function (a, b) {
          var sa = a.classList.contains('is-sold-out') ? 0 : 1;
          var sb = b.classList.contains('is-sold-out') ? 0 : 1;
          return (sb - sa) || (score(b) - score(a)) || (a.dataset.order - b.dataset.order);
        });
        sorted.forEach(function (el, i) {
          var plus = el.previousElementSibling && el.previousElementSibling.classList.contains('vt-outfit__plus') ? el.previousElementSibling : null;
          var show = i < limit;
          el.hidden = !show;
          if (plus) { plus.hidden = !show; list.appendChild(plus); }
          list.appendChild(el);
        });
      }

      items() {
        return Array.prototype.slice.call(this.querySelectorAll('[data-vt-outfit-item]')).filter(function (el) {
          return !el.hidden; // r6: spare items (colour matching) are not part of the outfit
        }).map(function (el) {
          var field = el.querySelector('[data-vt-outfit-variant]');
          var include = el.querySelector('[data-vt-outfit-include]');
          var option = field && field.tagName === 'SELECT' ? field.options[field.selectedIndex] : field;
          return {
            el: el,
            field: field,
            available: !!field,
            included: !!field && (!include || include.checked),
            id: field ? parseInt(field.value, 10) || 0 : 0,
            price: option && option.getAttribute('data-price') ? parseInt(option.getAttribute('data-price'), 10) : null
          };
        });
      }

      update() {
        var total = 0;
        var count = 0;
        this.items().forEach(function (item) {
          item.el.classList.toggle('is-excluded', item.available && !item.included);
          if (item.price !== null && item.id) {
            var priceEl = item.el.querySelector('[data-vt-outfit-price]');
            if (priceEl) priceEl.textContent = formatMoney(item.price);
          }
          if (item.included) {
            count += 1;
            if (item.price !== null && item.id) total += item.price;
          }
        });
        if (this.totalEl) this.totalEl.textContent = total ? formatMoney(total) : '–';
        if (this.button) this.button.disabled = count === 0;
      }

      setStatus(msg, isError) {
        if (!this.status) return;
        this.status.textContent = msg || '';
        this.status.classList.toggle('is-error', !!isError);
      }

      addAll() {
        var items = this.items().filter(function (i) { return i.included; });
        var missing = items.filter(function (i) { return !i.id; });
        if (missing.length) {
          missing.forEach(function (i) { i.el.classList.add('has-error'); });
          this.setStatus(tpl(S.outfitChoose, { title: missing[0].el.dataset.title }), true);
          if (missing[0].field && missing[0].field.focus) missing[0].field.focus();
          return;
        }
        if (!items.length) return;
        this.button.disabled = true;
        this.button.textContent = S.adding;
        this.setStatus('');
        var sections = ['page-header', 'cart-drawer'].map(function (sel) { return document.querySelector(sel); })
          .filter(function (el) { return el && el.dataset.sectionId; })
          .map(function (el) { return el.dataset.sectionId; });
        fetch(route('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: items.map(function (i) { return { id: i.id, quantity: 1 }; }),
            sections: sections.join(','),
            sections_url: window.location.pathname
          })
        })
          .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
          .then((res) => {
            if (!res.ok) throw new Error(friendlyError((res.data && (res.data.description || res.data.message)) || S.addError));
            this.button.textContent = this.buttonLabel;
            this.button.disabled = false;
            this.setStatus(S.outfitAdded);
            document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: items[0].id } }));
            openCartAfterAdd(this.button, res.data && res.data.sections);
          })
          .catch((err) => {
            this.button.textContent = this.buttonLabel;
            this.button.disabled = false;
            this.setStatus(err.message || S.addError, true);
          });
      }
    });
  }

  /* ------------------------------------ product strip (empty cart, r4) */
  function normaliseCollectionProduct(p) {
    var variants = p.variants || [];
    var cheapest = variants.slice().sort(function (a, b) { return parseFloat(a.price) - parseFloat(b.price); })[0] || {};
    var toCents = function (v) { return v ? Math.round(parseFloat(v) * 100) : 0; };
    return {
      handle: p.handle,
      title: p.title,
      available: variants.some(function (v) { return v.available; }),
      price: toCents(cheapest.price),
      compare_at_price: toCents(cheapest.compare_at_price),
      featured_image: p.images && p.images[0] ? p.images[0].src : null
    };
  }
  if (!customElements.get('vt-product-strip')) {
    customElements.define('vt-product-strip', class extends HTMLElement {
      connectedCallback() {
        if (this.observed) return;
        this.observed = true;
        var target = this.parentElement || this;
        if (!('IntersectionObserver' in window)) { this.load(); return; }
        this.io = new IntersectionObserver((entries) => {
          if (entries.some(function (e) { return e.isIntersecting; })) {
            this.io.disconnect();
            this.load();
          }
        });
        this.io.observe(target);
      }

      disconnectedCallback() {
        if (this.io) this.io.disconnect();
        this.observed = false;
      }

      load() {
        if (this.loaded) return;
        this.loaded = true;
        var limit = parseInt(this.dataset.limit, 10) || 4;
        var list = this.querySelector('[data-vt-list]');
        fetch(route('collections/' + encodeURIComponent(this.dataset.collection) + '/products.json?limit=24'), { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : { products: [] }; })
          .then((data) => {
            var products = (data.products || []).map(normaliseCollectionProduct).filter(function (p) { return p.available; }).slice(0, limit);
            if (!products.length || !list) return;
            list.innerHTML = products.map(function (p) { return cardHtml(p); }).join('');
            this.hidden = false;
          })
          .catch(function () {});
      }
    });
  }

  /* ------------------------------------------------ size guide popup (r4) */
  var sizeGuideDialog = null;
  function isSizeGuideLink(a) {
    if (!a || !a.getAttribute) return false;
    return /\/pages\/size-guide\/?([?#].*)?$/.test(a.getAttribute('href') || '');
  }
  function openSizeGuide(opener) {
    // Symmetry's own product-page modal (variant picker "Size guide"), when it is on the page
    var modal = document.querySelector('modal-dialog[id^="size-chart-"]');
    if (modal && typeof modal.open === 'function') {
      modal.open(opener);
      return;
    }
    if (!sizeGuideDialog) {
      sizeGuideDialog = document.createElement('dialog');
      sizeGuideDialog.className = 'vt-sg vt-sg--guide'; // r10 (E): styled like the size guide page (vt-r10-cards.css)
      sizeGuideDialog.setAttribute('aria-labelledby', 'vt-sg-title');
      sizeGuideDialog.innerHTML =
        '<div class="vt-sg__head"><h2 class="vt-sg__title" id="vt-sg-title">' + esc(S.sizeGuide) + '</h2>' +
        '<button type="button" class="vt-sg__close" data-vt-sg-close aria-label="' + esc(S.close) + '">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>' +
        '<div class="vt-sg__body rte" data-vt-sg-body><p>' + esc(S.loading) + '</p></div>';
      document.body.appendChild(sizeGuideDialog);
      sizeGuideDialog.addEventListener('click', function (evt) {
        if (evt.target === sizeGuideDialog || evt.target.closest('[data-vt-sg-close]')) sizeGuideDialog.close();
      });
      sizeGuideDialog.addEventListener('close', function () {
        document.documentElement.classList.remove('vt-sg-open');
        if (sizeGuideDialog.opener && sizeGuideDialog.opener.focus) sizeGuideDialog.opener.focus();
      });
      fetch(route('pages/size-guide?section_id=main-page'))
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var title = tmp.querySelector('.majortitle');
          var body = tmp.querySelector('.rte');
          if (title) sizeGuideDialog.querySelector('#vt-sg-title').textContent = title.textContent.trim();
          sizeGuideDialog.querySelector('[data-vt-sg-body]').innerHTML = (body ? body.innerHTML : '') +
            // r10 (E): the full page (drawing + converter) one tap away
            '<p class="vt-sg__more"><a href="' + esc(route('pages/size-guide')) + '">' + esc(S.sizeGuide) + ' <span aria-hidden="true">→</span></a></p>';
        })
        .catch(function () {
          sizeGuideDialog.querySelector('[data-vt-sg-body]').innerHTML = '<p><a href="' + esc(route('pages/size-guide')) + '">' + esc(S.sizeGuide) + '</a></p>';
        });
    }
    sizeGuideDialog.opener = opener;
    if (typeof sizeGuideDialog.showModal === 'function') {
      sizeGuideDialog.showModal();
      document.documentElement.classList.add('vt-sg-open');
      var closeBtn = sizeGuideDialog.querySelector('[data-vt-sg-close]');
      if (closeBtn) closeBtn.focus();
    } else {
      window.location.href = route('pages/size-guide');
    }
  }
  function initSizeGuide() {
    document.addEventListener('click', function (evt) {
      if (evt.defaultPrevented || evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
      var a = evt.target.closest && evt.target.closest('a[href]');
      if (!isSizeGuideLink(a)) return;
      // header, menus, footer and bottom bar keep normal navigation
      if (a.closest('.section-header, #pagefooter, .navigation, .vt-bnav')) return;
      // only product pages, quick buy and elements that opt in
      if (!a.closest('.js-product, .quickbuy-container, [data-vt-size-guide]') && !document.body.classList.contains('template-product')) return;
      evt.preventDefault();
      openSizeGuide(a);
    });
  }

  /* product gallery counter: moved to vt-product.js (round 9 T3) */

  /* -------------------------------------------------- WhatsApp request forms */
  if (!customElements.get('vt-whatsapp-form')) {
    customElements.define('vt-whatsapp-form', class extends HTMLElement {
      connectedCallback() {
        var form = this.querySelector('form');
        if (!form || this.bound) return;
        this.bound = true;
        form.addEventListener('submit', (evt) => {
          evt.preventDefault();
          var error = form.querySelector('[data-vt-form-error]');
          var status = form.querySelector('[data-vt-form-status]');
          var invalid = Array.prototype.slice.call(form.elements).filter(function (el) {
            if (!el.name) return false;
            if (el.type !== 'hidden') el.value = el.value.trim ? el.value.trim() : el.value;
            var bad = el.required && !el.value;
            el.setAttribute('aria-invalid', bad ? 'true' : 'false');
            return bad;
          });
          if (invalid.length) {
            if (error) error.hidden = false;
            invalid[0].focus();
            return;
          }
          if (error) error.hidden = true;
          var lines = [this.dataset.intro];
          Array.prototype.forEach.call(form.elements, function (el) {
            if (!el.name || !el.value || !el.dataset.label) return;
            var value = el.tagName === 'SELECT' ? el.options[el.selectedIndex].text : el.value;
            lines.push(el.dataset.label + ': ' + value);
          });
          var href = 'https://wa.me/' + this.dataset.number + '?text=' + encodeURIComponent(lines.join('\n'));
          if (status) status.textContent = S.whatsappOpening;
          var win = window.open(href, '_blank', 'noopener');
          if (!win) window.location.href = href;
        });
      }
    });
  }

  /* ------------------------------------------------------------ copy link */
  if (!customElements.get('vt-copy-link')) {
    customElements.define('vt-copy-link', class extends HTMLElement {
      connectedCallback() {
        var btn = this.querySelector('[data-vt-copy]');
        var label = this.querySelector('[data-vt-copy-label]');
        var status = this.querySelector('[data-vt-copy-status]');
        if (!btn || this.bound) return;
        this.bound = true;
        var url = this.dataset.url;
        var done = function (ok) {
          var msg = ok ? S.copied : S.copyFailed;
          if (label) label.textContent = ok ? S.copied : label.dataset.default;
          if (status) status.textContent = msg;
          btn.classList.toggle('is-copied', ok);
          window.setTimeout(function () {
            if (label) label.textContent = label.dataset.default;
            btn.classList.remove('is-copied');
          }, 2500);
          if (!ok) window.prompt(label ? label.dataset.default : '', url);
        };
        var shareTitle = this.dataset.shareTitle;
        btn.addEventListener('click', function () {
          // r5: phones open their own share sheet (gift guides); otherwise copy the link
          if (shareTitle && navigator.share) {
            navigator.share({ title: shareTitle, url: url }).catch(function () {});
            return;
          }
          if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(url).then(function () { done(true); }, function () { done(false); });
          } else {
            var ta = document.createElement('textarea');
            ta.value = url;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            var ok = false;
            try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
            ta.remove();
            done(ok);
          }
        });
      }
    });
  }

  /* ==================================================================== */
  /* Round 5                                                               */
  /* ==================================================================== */

  function jsonFetch(url, body) {
    return fetch(route(url), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (d) { return { ok: r.ok, data: d }; }, function () { return { ok: r.ok, data: {} }; });
    });
  }
  function parseJsonEl(el, fallback) {
    if (!el) return fallback;
    try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
  }

  /* ---- D1: "Save $14" under the product page price ---------------------- */
  function initSaveLine() {
    document.addEventListener('on:variant:change', function (evt) {
      var scope = evt.target.closest ? evt.target.closest('.js-product') : null;
      var box = scope && scope.querySelector('[data-vt-save]');
      if (!box) return;
      var v = evt.detail && evt.detail.variant;
      var save = v && v.compare_at_price > v.price ? v.compare_at_price - v.price : 0;
      if (save >= 100) save = Math.round(save / 100) * 100;
      var text = box.querySelector('[data-vt-save-text]');
      if (save > 0 && text) text.textContent = tpl(box.dataset.template, { amount: shortMoney(save) });
      box.hidden = !(save > 0);
    });
  }

  /* ---- D2: sizes on product cards (data already in the card) ------------- */
  function renderCardSizes(box, colour) {
    if (box.vtData === undefined) {
      try { box.vtData = JSON.parse(box.dataset.vtSizes || '[]'); } catch (e) { box.vtData = []; }
    }
    var order = [];
    var avail = Object.create(null);
    var found = false;
    box.vtData.forEach(function (run) {
      if (!run || run[0] !== colour) return;
      found = true;
      String(run[1] || '').split('|').forEach(function (s) {
        if (!s) return;
        var sold = s.charAt(0) === '~';
        var name = sold ? s.slice(1) : s;
        if (!(name in avail)) { avail[name] = !sold; order.push(name); } else if (!sold) { avail[name] = true; }
      });
    });
    if (!found) return;
    var list = box.querySelector('[data-vt-card-sizes-list]');
    if (list) {
      list.innerHTML = order.map(function (n) {
        return avail[n]
          ? '<span class="vt-card-sizes__chip">' + esc(n) + '</span>'
          : '<span class="vt-card-sizes__chip is-sold"><s>' + esc(n) + '</s><span class="visually-hidden"> ' + esc(S.cardSizesSold) + '</span></span>';
      }).join('');
    }
    box.dataset.colour = colour;
    box.classList.toggle('is-empty', !order.some(function (n) { return avail[n]; }));
    // r6: keep the always-visible sizes line (phones) in step with the colour shown
    var block = box.closest('product-block');
    var line = block && block.querySelector('[data-vt-card-sizes-line]');
    if (line) {
      // r7 (T2): every size of the colour shown; sold-out sizes crossed out (never hidden). All in stock and
      // consecutive whole numbers: short range "36–41".
      var inStock = order.filter(function (n) { return avail[n]; });
      var nums = order.map(Number);
      var html;
      if (inStock.length === order.length && order.length > 2 && nums.every(function (n, i) { return String(n) === order[i] && (i === 0 || n === nums[i - 1] + 1); })) {
        html = esc(order[0] + '–' + order[order.length - 1]);
      } else {
        html = order.map(function (n) {
          return avail[n] ? esc(n) : '<s>' + esc(n) + '</s><span class="visually-hidden"> ' + esc(S.cardSizesSold) + '</span>';
        }).join(' · ');
      }
      var lineList = line.querySelector('[data-vt-card-sizes-line-list]');
      if (lineList) lineList.innerHTML = html;
      line.hidden = !inStock.length;
    }
  }
  function initCardSizes() {
    var handler = function (evt) {
      var block = evt.target.closest && evt.target.closest('product-block');
      var box = block && block.querySelector('[data-vt-card-sizes]');
      if (!box) return;
      var item = evt.target.closest('.product-block-options--swatch .product-block-options__item');
      if (item) {
        if (box.dataset.colour !== item.dataset.optionItem || !box.vtDrawn) renderCardSizes(box, item.dataset.optionItem);
        box.vtDrawn = true;
      } else if (!box.vtDrawn) {
        box.vtDrawn = true;
        renderCardSizes(box, box.dataset.colour || '');
      }
    };
    document.addEventListener('mouseover', handler, { passive: true });
    document.addEventListener('click', handler);
    document.addEventListener('focusin', handler);
    // phones, one-per-row list: draw the visible lists
    var drawLists = function () {
      document.querySelectorAll('.product-grid--per-row-mob-1 [data-vt-card-sizes]').forEach(function (box) {
        if (box.vtDrawn) return;
        box.vtDrawn = true;
        renderCardSizes(box, box.dataset.colour || '');
      });
    };
    drawLists();
    document.addEventListener('click', function (evt) {
      if (evt.target.closest && evt.target.closest('.layout-switch, [data-vt-grid]')) window.setTimeout(drawLists, 50);
    });
    window.vtDrawCardLists = drawLists;
  }

  /* ---- D8: bag bounce + count pulse (heart pop is in initWishlist) ------- */
  function initMotion() {
    if (!document.documentElement.classList.contains('vt-motion')) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var bump = function () {
      document.querySelectorAll('.cart-link__icon, .cart-link__count, [data-vt-bnav-action="cart"] .vt-bnav__icon, [data-vt-cart-count]').forEach(function (el) {
        el.classList.remove('vt-bump');
        void el.offsetWidth;
        el.classList.add('vt-bump');
        el.addEventListener('animationend', function () { el.classList.remove('vt-bump'); }, { once: true });
      });
    };
    var pending = false;
    document.addEventListener('on:cart:add', function () { pending = true; bump(); });
    onCart(function () { if (pending) { pending = false; window.setTimeout(bump, 30); } });
  }

  /* free delivery hint: moved to vt-product.js (round 9 T3) */

  /* ---- U1: remember her size --------------------------------------------- */
  var SIZE_KEY = 'vt-size-memory';
  function readSizeMemory() {
    try {
      var o = JSON.parse(window.localStorage.getItem(SIZE_KEY) || '{}');
      return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
    } catch (e) { return {}; }
  }
  function writeSizeMemory(cat, size) {
    var o = readSizeMemory();
    if (size) o[cat] = String(size).slice(0, 20); else delete o[cat];
    try { window.localStorage.setItem(SIZE_KEY, JSON.stringify(o)); } catch (e) { /* storage blocked */ }
  }
  /* size memory (U1): moved to vt-product.js (round 9 T3) */

  /* ---- U3: change size / colour of a bag line ---------------------------- */
  if (!customElements.get('vt-line-variant')) {
    customElements.define('vt-line-variant', class extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.data = parseJsonEl(this.querySelector('[data-vt-lv-data]'), { v: [], p: {} });
        this.status = this.querySelector('[data-vt-lv-status]');
        this.selects = Array.prototype.slice.call(this.querySelectorAll('select[data-index]'));
        this.initial = this.selected();
        this.markAvailability();
        this.addEventListener('change', (evt) => {
          if (evt.target.matches && evt.target.matches('select[data-index]')) this.onChange(evt.target);
        });
      }

      selected() { return this.selects.map(function (s) { return s.value; }); }

      find(values) {
        return (this.data.v || []).filter(function (v) {
          return v[1].every(function (o, i) { return o === values[i]; });
        })[0];
      }

      markAvailability() {
        var current = this.selected();
        this.selects.forEach((sel, idx) => {
          Array.prototype.forEach.call(sel.options, (opt) => {
            if (!opt.dataset.label) opt.dataset.label = opt.textContent;
            var anyAvailable = (this.data.v || []).some(function (v) { return v[2] && v[1][idx] === opt.value; });
            opt.disabled = !anyAvailable && opt.value !== current[idx];
            opt.textContent = opt.dataset.label + (anyAvailable ? '' : ' — ' + S.soldOut);
          });
        });
      }

      reset() {
        this.selects.forEach((s, i) => { s.value = this.initial[i]; });
      }

      setStatus(msg) { if (this.status) this.status.textContent = msg || ''; }

      onChange(sel) {
        var idx = parseInt(sel.dataset.index, 10);
        var current = this.selected();
        var target = this.find(current);
        if (!target || !target[2]) {
          // keep the value she picked and move the other option(s) to the closest available variant
          var best = null;
          var bestScore = -1;
          (this.data.v || []).forEach(function (v) {
            if (!v[2] || v[1][idx] !== current[idx]) return;
            var score = v[1].filter(function (o, i) { return o === current[i]; }).length;
            if (score > bestScore) { best = v; bestScore = score; }
          });
          target = best;
          if (target) this.selects.forEach(function (s, i) { s.value = target[1][i]; });
        }
        if (!target) {
          this.setStatus(S.lineUnavailable);
          this.reset();
          return;
        }
        if (String(target[0]) === this.dataset.variant) return;
        this.swap(target[0]);
      }

      swap(id) {
        var qty = parseInt(this.dataset.qty, 10) || 1;
        var props = this.data.p && typeof this.data.p === 'object' && !Array.isArray(this.data.p) ? this.data.p : {};
        var item = { id: id, quantity: qty };
        if (Object.keys(props).length) item.properties = props;
        this.selects.forEach(function (s) { s.disabled = true; });
        this.classList.add('is-busy');
        this.setStatus(S.lineUpdating);
        var line = this.closest('.cart-item');
        if (line) line.classList.add('merge-item-refreshing');
        jsonFetch('cart/add.js', { items: [item] })
          .then((res) => {
            if (!res.ok) throw new Error(friendlyError((res.data && (res.data.description || res.data.message)) || S.lineError));
            return jsonFetch('cart/change.js', { id: this.dataset.key, quantity: 0 });
          })
          .then((res) => {
            if (!res.ok) throw new Error(S.lineError);
            announce(S.lineChanged);
            document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
          })
          .catch((err) => {
            if (line) line.classList.remove('merge-item-refreshing');
            this.classList.remove('is-busy');
            this.selects.forEach(function (s) { s.disabled = false; });
            this.reset();
            this.setStatus(err.message || S.lineError);
          });
      }
    });
  }

  /* "Watch it worn" video: moved to vt-product.js (round 9 T3) */
  /* load a third-party script once (shared: vtCore.loadScriptOnce; used by the video in vt-product.js) */
  var loadedScripts = {};
  function loadScriptOnce(src, force) {
    if (loadedScripts[src] && !force) return loadedScripts[src];
    loadedScripts[src] = new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = resolve;
      document.body.appendChild(s);
    });
    return loadedScripts[src];
  }

  /* shop similar: moved to vt-product.js (round 9 T3) */

  /* ---- T4/T5: search — Arabic words and common typos --------------------- */
  var SEARCH_PHRASES = [
    ['حذاء رياضي', 'sneakers'], ['احذية رياضية', 'sneakers'], ['حذاء رياضة', 'sneakers'], ['كعب عالي', 'high heels'],
    ['hand bag', 'handbag'], ['hand bags', 'handbags']
  ];
  var SEARCH_WORDS = {
    'كعب': 'heels', 'كعوب': 'heels', 'صندل': 'sandals', 'صنادل': 'sandals', 'سنيكرز': 'sneakers', 'سنيكر': 'sneakers',
    'حقيبة': 'bags', 'حقيبه': 'bags', 'حقائب': 'bags', 'شنطة': 'bags', 'شنطه': 'bags', 'شنط': 'bags',
    'كلتش': 'clutch', 'جزمة': 'boots', 'جزمه': 'boots', 'جزم': 'boots', 'بوت': 'boots', 'بوط': 'boots', 'بوتات': 'boots',
    'شبشب': 'slippers', 'شباشب': 'slippers', 'عروس': 'bridal', 'زفاف': 'bridal', 'مسطح': 'flats', 'مسطحة': 'flats', 'مسطحه': 'flats',
    'لوفر': 'loafers', 'باليرينا': 'ballerina',
    // colours (alef already normalised: أسود -> اسود)
    'اسود': 'black', 'ابيض': 'white', 'بيج': 'beige', 'ذهبي': 'gold', 'فضي': 'silver', 'احمر': 'red', 'وردي': 'pink', 'زهري': 'pink',
    'بني': 'brown', 'كحلي': 'navy', 'اخضر': 'green', 'ازرق': 'blue', 'نود': 'nude', 'عنابي': 'burgundy',
    sandels: 'sandals', sandel: 'sandals', sandles: 'sandals', snekers: 'sneakers', sneeker: 'sneakers', sneekers: 'sneakers',
    heals: 'heels', bots: 'boots', cluch: 'clutch', hanbag: 'handbag', hanbags: 'handbags'
  };
  function normaliseSearch(q) {
    var original = String(q == null ? '' : q);
    if (!cfg.searchNormalise || !original.trim()) return original;
    var text = ' ' + original.trim().toLowerCase()
      .replace(/[ً-ْـ]/g, '') // Arabic diacritics + tatweel
      .replace(/[أإآ]/g, 'ا') // alef forms -> bare alef
      .replace(/\s+/g, ' ') + ' ';
    var pre = text.trim();
    SEARCH_PHRASES.forEach(function (p) { text = text.split(' ' + p[0] + ' ').join(' ' + p[1] + ' '); });
    var base = text.trim();
    var out = base.split(' ').map(function (tok) {
      if (Object.prototype.hasOwnProperty.call(SEARCH_WORDS, tok)) return SEARCH_WORDS[tok];
      var bare = tok.replace(/^(وال|بال|لل|ال)/, ''); // وال بال لل ال
      if (bare !== tok && Object.prototype.hasOwnProperty.call(SEARCH_WORDS, bare)) return SEARCH_WORDS[bare];
      return tok;
    }).join(' ');
    // only replace when something was actually translated or fixed
    return out !== pre ? out : original;
  }
  window.vtNormaliseSearch = normaliseSearch;
  function isSearchForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    return /\/search\/?(\?.*)?$/.test(action) && !!form.querySelector('input[name="q"]');
  }
  function initSearch() {
    if (!cfg.searchNormalise) return;
    document.addEventListener('submit', function (evt) {
      var form = evt.target;
      if (!isSearchForm(form)) return;
      var input = form.querySelector('input[name="q"]');
      var original = input.value;
      var fixed = normaliseSearch(original);
      var old = form.querySelector('input[name="vt_orig"]');
      if (old) old.remove();
      if (fixed === original) return;
      input.value = fixed;
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'vt_orig';
      hidden.value = original.slice(0, 80);
      form.appendChild(hidden);
      // show what she typed again if the page comes back from the back/forward cache
      window.addEventListener('pageshow', function () { input.value = original; }, { once: true });
    }, true);
    // 30 Sep 2026: a search page opened straight from a link (shared, saved, or typed in the address bar) with an
    // Arabic word skipped the form, so حقيبة found nothing. Do the same word swap here, once, and keep her word.
    (function () {
      if (!/\/search\/?$/.test(window.location.pathname)) return;
      var p0 = new URLSearchParams(window.location.search);
      var q0 = p0.get('q');
      if (!q0 || p0.get('vt_orig')) return;
      var fixed0 = normaliseSearch(q0);
      if (fixed0 === q0) return;
      p0.set('q', fixed0);
      p0.set('vt_orig', q0.slice(0, 80));
      window.location.replace(window.location.pathname + '?' + p0.toString());
    })();
    var note = document.querySelector('[data-vt-search-note]');
    if (note) {
      var params = new URLSearchParams(window.location.search);
      var orig = params.get('vt_orig');
      var q = params.get('q');
      if (orig && q && orig !== q) {
        note.textContent = tpl(note.dataset.template, { query: q, original: orig });
        note.hidden = false;
      }
    }
  }

  /* ==================================================================== */
  /* Round 6                                                               */
  /* ==================================================================== */

  function storageGet(store, key) {
    try { return window[store].getItem(key); } catch (e) { return null; }
  }
  function storageSet(store, key, value) {
    try { window[store].setItem(key, value); } catch (e) { /* storage blocked */ }
  }
  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---- T5: friendly add-to-bag / cart messages ---------------------------- */
  function friendlyError(msg) {
    var m = String(msg == null ? '' : msg).trim();
    if (!cfg.friendlyErrors) return m || S.addError;
    var partial = m.match(/only (\d+) (?:items?|pieces?) (?:were|was) added/i);
    if (partial) return tpl(S.errPartial, { count: partial[1] });
    if (/sold out|out of stock|no longer available|isn.t available|not available|unavailable|نفد|غير متوفر|غير متاح/i.test(m)) return S.errSoldOut;
    if (/in your cart|already|only add|maximum|max\b|limit|quantity|الكمية|سلتك|سلة/i.test(m)) return S.errQty;
    if (/variant|cannot find|can.t find|required|parameter|missing/i.test(m)) return S.errChoose;
    if (!m || /^\d{3}$/.test(m) || /network|failed to fetch|load failed|timeout|http error|status/i.test(m)) return S.errGeneric;
    return m;
  }
  function initFriendlyErrors() {
    if (!cfg.friendlyErrors) return;
    document.addEventListener('on:cart:error', function (evt) {
      var form = evt.target && evt.target.closest ? evt.target.closest('product-form, sticky-atc, .js-product') : null;
      var box = form && form.querySelector('.js-form-error');
      if (!box || box.hidden) return;
      var nice = friendlyError(box.textContent);
      if (nice && box.textContent !== nice) box.textContent = nice;
    });
    // messages shown without an error event (e.g. "Only 8 items were added…" when the add partly worked)
    if (!('MutationObserver' in window)) return;
    var watch = function (box) {
      if (box.vtWatched) return;
      box.vtWatched = true;
      new MutationObserver(function () {
        var text = box.textContent.trim();
        if (!text || box.hidden) return;
        var nice = friendlyError(text);
        if (nice && nice !== text) box.textContent = nice;
      }).observe(box, { childList: true, characterData: true, subtree: true });
    };
    document.querySelectorAll('.js-form-error').forEach(watch);
    document.addEventListener('on:quickbuy:after-open', function (evt) {
      (evt.target.querySelectorAll ? evt.target : document).querySelectorAll('.js-form-error').forEach(watch);
    });
  }

  /* sticky bar colour + size: moved to vt-product.js (round 9 T3) */

  /* ---- hover audit: tap equivalents on product cards ----------------------- */
  function initCardTaps() {
    // "+ 2 more" colours: tap/Enter shows the hidden colour dots (desktop keeps hover)
    var reveal = function (label) {
      var box = label.closest('.product-block-options');
      if (box) { box.classList.add('vt-options-open'); label.setAttribute('aria-expanded', 'true'); }
    };
    document.addEventListener('click', function (evt) {
      var label = evt.target.closest && evt.target.closest('[data-vt-more-options]');
      if (label) { evt.preventDefault(); reveal(label); return; }
      // colour dot tapped: show its name for a moment (the name is a hover tooltip on computers)
      var dot = evt.target.closest && evt.target.closest('.product-block-options--swatch .product-block-options__item');
      if (dot && !(window.matchMedia && window.matchMedia('(hover: hover)').matches)) {
        dot.parentNode.querySelectorAll('.vt-show-name').forEach(function (d) { d.classList.remove('vt-show-name'); });
        dot.classList.add('vt-show-name');
        window.clearTimeout(dot.vtNameTimer);
        dot.vtNameTimer = window.setTimeout(function () { dot.classList.remove('vt-show-name'); }, 1800);
      }
    });
    document.addEventListener('keydown', function (evt) {
      if (evt.key !== 'Enter' && evt.key !== ' ') return;
      var label = evt.target.closest && evt.target.closest('[data-vt-more-options]');
      if (label) { evt.preventDefault(); reveal(label); }
    });
  }

  /* phone grid switch: moved to vt-collection.js (round 9 T3) */

  /* ---- after Symmetry re-renders the collection (filters / sort) ---------- */
  function onListingRefresh(fn) {
    var listing = document.querySelector('filter-container .collection-listing, filter-container [data-ajax-container]');
    if (!listing || !('MutationObserver' in window)) return;
    var timer = null;
    new MutationObserver(function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(fn, 60);
    }).observe(listing, { childList: true });
  }

  /* load more: moved to vt-collection.js (round 9 T3) */

  /* show my size chip: moved to vt-collection.js (round 9 T3) */

  /* ---- U5: compare 2–3 shoes --------------------------------------------- */
  var CMP_KEY = 'vt-compare';
  var CMP_MAX = 3;
  function readCompare() {
    try {
      var v = JSON.parse(storageGet('sessionStorage', CMP_KEY) || '[]');
      return Array.isArray(v) ? v.filter(function (x) { return x && typeof x.h === 'string'; }).slice(0, CMP_MAX) : [];
    } catch (e) { return []; }
  }
  function writeCompare(list) {
    storageSet('sessionStorage', CMP_KEY, JSON.stringify(list));
    syncCompare();
  }
  var cmpBar = null;
  var cmpDialog = null;
  function syncCompare() {
    if (!cfg.compare) return;
    var list = readCompare();
    var handles = list.map(function (x) { return x.h; });
    document.querySelectorAll('[data-vt-compare]').forEach(function (box) {
      var on = handles.indexOf(box.dataset.handle) > -1;
      if (box.checked !== on) box.checked = on;
    });
    if (!list.length) {
      if (cmpBar) cmpBar.hidden = true;
      document.body.classList.remove('vt-has-compare');
      return;
    }
    if (!cmpBar) {
      cmpBar = document.createElement('div');
      cmpBar.className = 'vt-cmp-bar';
      cmpBar.setAttribute('role', 'region');
      cmpBar.setAttribute('aria-label', S.compareLabel);
      cmpBar.innerHTML = '<p class="vt-cmp-bar__text" data-vt-cmp-text aria-live="polite"></p>' +
        '<div class="vt-cmp-bar__actions"><button type="button" class="btn btn--primary btn--compact vt-cmp-bar__go" data-vt-cmp-open></button>' +
        '<button type="button" class="vt-cmp-bar__clear" data-vt-cmp-clear>' + esc(S.compareClear) + '</button></div>';
      document.body.appendChild(cmpBar);
      cmpBar.addEventListener('click', function (evt) {
        if (evt.target.closest('[data-vt-cmp-clear]')) { writeCompare([]); announce(S.compareCleared); }
        if (evt.target.closest('[data-vt-cmp-open]')) openCompare(evt.target.closest('[data-vt-cmp-open]'));
      });
    }
    cmpBar.querySelector('[data-vt-cmp-text]').textContent = tpl(S.compareCount, { count: list.length, max: CMP_MAX });
    var go = cmpBar.querySelector('[data-vt-cmp-open]');
    go.textContent = list.length < 2 ? S.compareAddOne : S.compareOpen;
    go.disabled = list.length < 2;
    cmpBar.hidden = false;
    document.body.classList.add('vt-has-compare');
  }
  window.vtSyncCompare = function () { if (cfg.compare && document.querySelector('[data-vt-compare]')) syncCompare(); };

  function compareData(handle) {
    return fetch(route('products/' + encodeURIComponent(handle) + '?section_id=vt-compare-data'))
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        return parseJsonEl(tmp.querySelector('[data-vt-compare-json]'), null);
      })
      .catch(function () { return null; });
  }
  function openCompare(opener) {
    var list = readCompare();
    if (list.length < 2) return;
    if (!cmpDialog) {
      cmpDialog = document.createElement('dialog');
      cmpDialog.className = 'vt-sg vt-cmp';
      cmpDialog.setAttribute('aria-labelledby', 'vt-cmp-title');
      cmpDialog.innerHTML = '<div class="vt-sg__head"><h2 class="vt-sg__title" id="vt-cmp-title">' + esc(S.compareTitle) + '</h2>' +
        '<button type="button" class="vt-sg__close" data-vt-cmp-close aria-label="' + esc(S.close) + '">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>' +
        '<div class="vt-cmp__body" data-vt-cmp-body></div>';
      document.body.appendChild(cmpDialog);
      cmpDialog.addEventListener('click', function (evt) {
        if (evt.target === cmpDialog || evt.target.closest('[data-vt-cmp-close]')) { cmpDialog.close(); return; }
        var rm = evt.target.closest('[data-vt-cmp-remove]');
        if (rm) {
          writeCompare(readCompare().filter(function (x) { return x.h !== rm.dataset.vtCmpRemove; }));
          if (readCompare().length < 2) cmpDialog.close(); else renderCompare();
        }
      });
      cmpDialog.addEventListener('close', function () {
        document.documentElement.classList.remove('vt-sg-open');
        if (cmpDialog.opener && cmpDialog.opener.isConnected && cmpDialog.opener.focus) cmpDialog.opener.focus();
      });
    }
    cmpDialog.opener = opener;
    renderCompare();
    if (typeof cmpDialog.showModal === 'function') {
      cmpDialog.showModal();
      document.documentElement.classList.add('vt-sg-open');
      cmpDialog.querySelector('[data-vt-cmp-close]').focus();
    }
  }
  function renderCompare() {
    var body = cmpDialog.querySelector('[data-vt-cmp-body]');
    var list = readCompare();
    body.innerHTML = '<p class="vt-cmp__loading">' + esc(S.loading) + '</p>';
    body.setAttribute('aria-busy', 'true');
    Promise.all(list.map(function (x) { return compareData(x.h); })).then(function (rows) {
      rows = rows.filter(Boolean);
      body.removeAttribute('aria-busy');
      if (!rows.length) { body.innerHTML = '<p>' + esc(S.errGeneric) + '</p>'; return; }
      var dash = '<span aria-label="' + esc(S.compareNone) + '">–</span>';
      var cell = function (v) { return v ? esc(v) : dash; };
      var head = '<tr><td class="vt-cmp__corner"></td>' + rows.map(function (p) {
        var img = p.image ? '<img src="' + esc(p.image) + '" alt="" width="180" height="180" loading="lazy">' : '';
        return '<th scope="col" class="vt-cmp__product"><a href="' + esc(route(String(p.url).replace(/^\//, ''))) + '" class="vt-cmp__link">' +
          '<span class="vt-cmp__img">' + img + '</span><span class="vt-cmp__name">' + esc(p.title) + '</span></a>' +
          '<button type="button" class="vt-cmp__remove" data-vt-cmp-remove="' + esc(p.handle) + '">' + esc(S.compareRemove) + '<span class="visually-hidden">: ' + esc(p.title) + '</span></button></th>';
      }).join('') + '</tr>';
      var row = function (label, fn) {
        return '<tr><th scope="row">' + esc(label) + '</th>' + rows.map(function (p) { return '<td>' + fn(p) + '</td>'; }).join('') + '</tr>';
      };
      var minW = (window.innerWidth < 768 ? 76 : 96) + rows.length * (window.innerWidth < 768 ? 136 : 180);
      var html = '<div class="vt-cmp__scroll"><table class="vt-cmp__table" style="min-width:' + minW + 'px"><caption class="visually-hidden">' + esc(S.compareTitle) + '</caption>' +
        '<thead>' + head + '</thead><tbody>' +
        row(S.comparePrice, function (p) {
          var price = formatMoney(p.price);
          if (p.priceMax && p.priceMax > p.price) price += ' – ' + formatMoney(p.priceMax);
          var was = p.compareAt && p.compareAt > p.price ? ' <s class="vt-cmp__was">' + esc(formatMoney(p.compareAt)) + '</s>' : '';
          return '<span class="vt-cmp__price">' + esc(price) + '</span>' + was;
        }) +
        row(S.compareHeel, function (p) { return cell(p.heel); }) +
        row(S.compareFit, function (p) { return cell(p.fit); }) +
        row(S.compareColours, function (p) { return cell((p.colours || []).filter(Boolean).join(', ')); }) +
        row(S.compareSizes, function (p) {
          if (!p.available || !(p.sizes || []).length) return esc(S.soldOut);
          return '<span class="vt-cmp__sizes">' + p.sizes.map(function (s) { return '<span class="vt-card-sizes__chip">' + esc(s) + '</span>'; }).join('') + '</span>';
        }) +
        '<tr><th scope="row"><span class="visually-hidden">' + esc(S.compareView) + '</span></th>' + rows.map(function (p) {
          return '<td><a class="btn btn--secondary btn--compact" href="' + esc(route(String(p.url).replace(/^\//, ''))) + '">' + esc(S.compareView) + '<span class="visually-hidden">: ' + esc(p.title) + '</span></a></td>';
        }).join('') + '</tr>' +
        '</tbody></table></div>';
      body.innerHTML = html;
    });
  }
  function initCompare() {
    if (!cfg.compare) return;
    document.addEventListener('change', function (evt) {
      var box = evt.target;
      if (!box.matches || !box.matches('[data-vt-compare]')) return;
      var list = readCompare();
      var h = box.dataset.handle;
      if (box.checked) {
        if (list.length >= CMP_MAX) {
          box.checked = false;
          announce(tpl(S.compareMax, { max: CMP_MAX }));
          if (cmpBar) {
            var t = cmpBar.querySelector('[data-vt-cmp-text]');
            if (t) t.textContent = tpl(S.compareMax, { max: CMP_MAX });
          }
          return;
        }
        if (!list.some(function (x) { return x.h === h; })) list.push({ h: h, t: box.dataset.title || h });
      } else {
        list = list.filter(function (x) { return x.h !== h; });
      }
      writeCompare(list);
    });
    if (document.querySelector('[data-vt-compare]') || readCompare().length) syncCompare();
  }

  /* ---- U7: back to top ----------------------------------------------------- */
  function initBackToTop() {
    if (!cfg.backToTop) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'vt-top';
    btn.hidden = true;
    btn.setAttribute('aria-label', S.backToTop);
    btn.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 19V5M5.5 11.5 12 5l6.5 6.5"/></svg>';
    document.body.appendChild(btn);
    var ticking = false;
    var check = function () {
      ticking = false;
      var show = window.scrollY > window.innerHeight * 2;
      if (btn.hidden === show) btn.hidden = !show;
    };
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(check); }
    }, { passive: true });
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
      var target = document.querySelector('.logo__link') || document.querySelector('a[href]');
      if (target) window.setTimeout(function () { target.focus({ preventScroll: true }); }, reducedMotion() ? 0 : 400);
    });
  }

  /* ---- T1: prefetch on hover / touch (browsers without speculation rules) - */
  function initPrefetch() {
    if (!cfg.prefetch) return;
    if (window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) return;
    var conn = navigator.connection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;
    var done = {};
    var count = 0;
    var MAX = 12;
    var timer = null;
    var eligible = function (a) {
      if (!a || !a.href || a.origin !== window.location.origin) return false;
      if (a.hasAttribute('download') || a.target === '_blank' || a.closest('form, [data-no-prefetch]')) return false;
      var raw = a.getAttribute('href') || '';
      if (raw.charAt(0) === '#' || /^(mailto|tel|javascript):/i.test(raw)) return false;
      var path = a.pathname;
      if (/\/(cart|checkout|checkouts|account)(\/|$)/.test(path)) return false;
      return /\/(products|collections)\//.test(path);
    };
    var prefetch = function (a) {
      var url = a.href.split('#')[0];
      if (done[url] || count >= MAX || url === window.location.href.split('#')[0]) return;
      done[url] = true;
      count += 1;
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      document.head.appendChild(link);
    };
    document.addEventListener('mouseover', function (evt) {
      var a = evt.target.closest && evt.target.closest('a[href]');
      if (!eligible(a)) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () { prefetch(a); }, 90);
    }, { passive: true });
    document.addEventListener('mouseout', function (evt) {
      var a = evt.target.closest && evt.target.closest('a[href]');
      // only cancel when the pointer really leaves the link (not when moving between its inner parts)
      if (a && evt.relatedTarget && a.contains(evt.relatedTarget)) return;
      window.clearTimeout(timer);
    }, { passive: true });
    document.addEventListener('touchstart', function (evt) {
      var a = evt.target.closest && evt.target.closest('a[href]');
      if (eligible(a)) prefetch(a);
    }, { passive: true });
  }

  /* ---- U1/U2: size converter (UK/US → EU, foot length → EU) --------------- */
  if (!customElements.get('vt-size-converter')) {
    customElements.define('vt-size-converter', class extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.rows = String(this.dataset.rows || '').split(';').map(function (r) {
          var c = r.split(',');
          return { eu: c[0], uk: c[1], us: c[2], cm: parseFloat(c[3]) };
        }).filter(function (r) { return r.eu && !isNaN(r.cm); });
        this.system = this.querySelector('[data-vt-conv-system]');
        this.size = this.querySelector('[data-vt-conv-size]');
        this.result = this.querySelector('[data-vt-conv-result]');
        this.cm = this.querySelector('[data-vt-conv-cm]');
        this.cmResult = this.querySelector('[data-vt-conv-cm-result]');
        if (!this.rows.length || !this.system || !this.size) return;
        this.fillSizes();
        this.system.addEventListener('change', () => { this.fillSizes(); this.convert(); });
        this.size.addEventListener('change', () => this.convert());
        if (this.cm) this.cm.addEventListener('input', () => this.fromCm());
        this.initMine();
      }

      /* r11 (U5): the EU size she converted is kept on her phone / computer (localStorage "vt_conv_size") and
         marked in the size tables next time; the product page shows a small "Your size" tag on that size button
         (assets/vt-product.js, never chosen for her). "Forget my size" clears it. Only with data-vt-remember
         (Theme settings > Verona Tempo > "'Your size' from the size guide", snippets/vt-size-convert.liquid). */
      initMine() {
        this.mine = this.hasAttribute('data-vt-remember') ? this.querySelector('[data-vt-conv-mine]') : null;
        if (!this.mine) return;
        var forget = this.mine.querySelector('[data-vt-conv-forget]');
        if (forget) forget.addEventListener('click', () => { this.saveMine(''); announce(this.dataset.forgotten || ''); if (this.size) this.size.focus(); });
        var saved = '';
        try { saved = window.localStorage.getItem('vt_conv_size') || ''; } catch (e) { saved = ''; }
        if (saved && this.rows.some(function (r) { return r.eu === saved; })) { this.mark(saved); this.showMine(saved); }
        document.addEventListener('vt:conv-size', (evt) => {
          if (evt.detail && evt.detail.from === this) return;
          var eu = evt.detail ? evt.detail.eu : '';
          this.mark(eu);
          this.showMine(eu);
        });
      }

      showMine(eu) {
        if (!this.mine) return;
        var text = this.mine.querySelector('[data-vt-conv-mine-text]');
        if (text) text.textContent = eu ? tpl(this.dataset.mineText || '', { size: eu }) : '';
        this.mine.hidden = !eu;
      }

      saveMine(eu) {
        if (!this.mine) return;
        try {
          if (eu) window.localStorage.setItem('vt_conv_size', eu); else window.localStorage.removeItem('vt_conv_size');
        } catch (e) { /* storage blocked: still marked for this visit */ }
        if (!eu) this.mark('');
        this.showMine(eu);
        document.dispatchEvent(new CustomEvent('vt:conv-size', { detail: { eu: eu, from: this } }));
      }

      fillSizes() {
        var sys = this.system.value;
        var keep = this.size.value;
        var first = this.size.options[0];
        this.size.innerHTML = '';
        this.size.appendChild(first);
        this.rows.forEach((r) => {
          var o = document.createElement('option');
          o.value = r[sys];
          o.textContent = sys.toUpperCase() + ' ' + r[sys];
          this.size.appendChild(o);
        });
        this.size.value = keep && this.rows.some(function (r) { return r[sys] === keep; }) ? keep : '';
      }

      mark(eu) {
        this.querySelectorAll('tr[data-eu]').forEach(function (tr) { tr.classList.toggle('is-match', tr.dataset.eu === eu); });
        // r11 (U5): also the size table of the size guide page shown next to it (first column = EU)
        if (!this.mine) return;
        var box = this.closest('.modal__content, .rte, .vt-sg, main') || document;
        box.querySelectorAll('table:not(.vt-conv__table) tbody tr').forEach(function (tr) {
          var cell = tr.querySelector('td, th');
          tr.classList.toggle('vt-conv-match', !!eu && !!cell && cell.textContent.trim() === eu);
        });
      }

      convert() {
        var sys = this.system.value;
        var val = this.size.value;
        var row = this.rows.filter(function (r) { return r[sys] === val; })[0];
        this.result.textContent = row ? tpl(S.convResult, { from: sys.toUpperCase() + ' ' + val, eu: row.eu }) : '';
        this.mark(row ? row.eu : (this.mine && !this.mine.hidden ? this.savedEu() : ''));
        if (row) this.saveMine(row.eu);
      }

      savedEu() {
        try { return window.localStorage.getItem('vt_conv_size') || ''; } catch (e) { return ''; }
      }

      fromCm() {
        var v = parseFloat(String(this.cm.value).replace(',', '.'));
        if (!this.cmResult) return;
        if (isNaN(v) || v <= 0) { this.cmResult.textContent = ''; this.mark(''); return; }
        var first = this.rows[0];
        var last = this.rows[this.rows.length - 1];
        if (v > last.cm) {
          this.cmResult.textContent = tpl(S.convTooBig, { eu: last.eu, cm: last.cm });
          this.mark('');
          return;
        }
        var row = this.rows.filter(function (r) { return r.cm >= v - 0.001; })[0] || first;
        this.cmResult.textContent = v < first.cm ? tpl(S.convTooSmall, { eu: first.eu, cm: first.cm }) : tpl(S.convCmResult, { cm: v, eu: row.eu });
        this.mark(row.eu);
        if (v >= first.cm) this.saveMine(row.eu);
      }
    });
  }
  function initConverterLink() {
    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('[data-vt-open-converter]');
      if (!btn) return;
      var id = btn.dataset.vtOpenConverter;
      window.setTimeout(function () {
        var conv = document.getElementById(id);
        if (!conv) return;
        conv.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
        var first = conv.querySelector('select, input');
        if (first) first.focus({ preventScroll: true });
      }, 350);
    });
  }

  /* ==================================================================== */
  /* Round 7                                                               */
  /* ==================================================================== */

  var COLOUR_NAMES = (cfg.colourNames && cfg.colourNames.length ? cfg.colourNames : ['Color', 'Colour'])
    .map(function (n) { return String(n).trim().toLowerCase(); }).filter(Boolean);

  /* the colour option of a product form: { index, name } (name as shown, e.g. "Color" / "اللون") */
  function colourOption(productEl) {
    var sels = productEl ? productEl.querySelectorAll('variant-picker .option-selector') : [];
    for (var i = 0; i < sels.length; i += 1) {
      var name = String(sels[i].dataset.option || '');
      var legend = sels[i].querySelector('legend, label.label');
      var isColour = COLOUR_NAMES.indexOf(name.trim().toLowerCase()) > -1 || !!sels[i].querySelector('.js-color-text');
      if (isColour) return { index: i, name: legend ? legend.textContent.split(':')[0].trim() : name };
    }
    return null;
  }

  /* ---- D10: colour name on the main photo when she picks another colour ---- */
  function initColourCaption() {
    if (!cfg.colourCaption) return;
    // remember the colour shown when the page opens, so only a real change shows the caption
    var prime = function (cap) {
      if (cap.dataset.value !== undefined) return;
      var product = cap.closest('.js-product');
      var opt = colourOption(product);
      if (!opt) return;
      var sel = product.querySelectorAll('variant-picker .option-selector')[opt.index];
      var checked = sel && sel.querySelector('input[type="radio"]:checked');
      if (checked) cap.dataset.value = checked.value;
    };
    document.querySelectorAll('[data-vt-colour-caption]').forEach(prime);
    document.addEventListener('on:variant:change', function (evt) {
      var product = evt.target.closest && evt.target.closest('.js-product');
      var cap = product && product.querySelector('[data-vt-colour-caption]');
      var v = evt.detail && evt.detail.variant;
      if (!cap || !v || !v.options) return;
      prime(cap);
      var opt = colourOption(product);
      if (!opt) return;
      var value = v.options[opt.index];
      if (!value || value === cap.dataset.value) return;
      cap.dataset.value = value;
      cap.textContent = opt.name + ': ' + value;
      cap.hidden = false;
      cap.classList.remove('is-out');
      void cap.offsetWidth;
      cap.classList.add('is-in');
      window.clearTimeout(cap.vtTimer);
      cap.vtTimer = window.setTimeout(function () {
        cap.classList.remove('is-in');
        cap.classList.add('is-out');
      }, 2600);
    });
  }

  /* ---- U2: "In stock" / "Sold out" line for the chosen size and colour ------ */
  function initStockLine() {
    if (!cfg.stockLine) return;
    document.addEventListener('on:variant:change', function (evt) {
      var product = evt.target.closest && evt.target.closest('.js-product');
      var line = product && product.querySelector('[data-vt-stock-line]');
      if (!line) return;
      var v = evt.detail && evt.detail.variant;
      var text = line.querySelector('[data-vt-stock-text]');
      if (!v) { line.hidden = true; return; }
      line.hidden = false;
      line.classList.toggle('vt-stockline--in', !!v.available);
      line.classList.toggle('vt-stockline--out', !v.available);
      if (text) text.textContent = v.available ? line.dataset.in : line.dataset.out;
    });
  }

  /* similar price row: moved to vt-product.js (round 9 T3) */

  /* ---- E2: colour of the season — products in that colour ------------------ */
  function initColourSeason() {
    var box = document.querySelector('[data-vt-colour-season]');
    if (!box) return;
    var slot = box.querySelector('[data-vt-colour-slot]');
    var colour = String(box.dataset.colour || '').trim();
    var term = String(box.dataset.colourKey || colour).trim();
    if (/[؀-ۿ]/.test(term)) {
      // Arabic colour word → the English one used in the product options (search helper from round 5)
      var was = cfg.searchNormalise;
      cfg.searchNormalise = true;
      term = normaliseSearch(term);
      cfg.searchNormalise = was;
    }
    if (!term || !slot) return;
    var seeAll = route('search') + '?q=' + encodeURIComponent(term) + '&type=product&filter.v.availability=1';
    var linkHtml = '<a class="btn btn--secondary" href="' + esc(seeAll) + '">' + esc(tpl(S.colourSeeAll, { colour: colour })) + '</a>';
    if (cfg.skeletons) slot.innerHTML = '<ul class="vt-skel-row" aria-hidden="true">' + skeletonCards(4) + '</ul>';
    fetch(seeAll + '&section_id=vt-colour-results', { headers: { Accept: 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var res = tmp.querySelector('[data-vt-colour-results]');
        var countEl = res && res.querySelector('[data-vt-colour-count]');
        var count = countEl ? parseInt(countEl.getAttribute('data-vt-colour-count'), 10) || 0 : 0;
        if (!res || !count) {
          slot.innerHTML = '<p>' + esc(tpl(S.colourNone, { colour: colour })) + '</p>';
          return;
        }
        res.querySelectorAll('[data-cc-animate]').forEach(function (el) { el.removeAttribute('data-cc-animate'); });
        slot.innerHTML = '';
        slot.appendChild(res);
        var more = document.createElement('p');
        more.className = 'vt-cseason__more';
        more.innerHTML = linkHtml;
        slot.appendChild(more);
        if (window.vtDrawCardLists) window.vtDrawCardLists();
        if (window.vtSyncCompare) window.vtSyncCompare();
      })
      .catch(function () { slot.innerHTML = '<p>' + linkHtml + '</p>'; });
  }

  /* ---- U3: "Continue shopping: Heels" for returning visitors ------------------ */
  var LAST_COLL_KEY = 'vt-last-collection';
  function initContinueChip() {
    if (!cfg.continueChip) return;
    var lang = document.documentElement.lang || '';
    if (document.body.classList.contains('template-collection')) {
      storageSet('sessionStorage', 'vt-coll-this-visit', '1');
      var h1 = document.querySelector('.text-overlay__title, h1.pagetitle, #content h1');
      var title = h1 ? h1.textContent.trim() : '';
      var path = window.location.pathname;
      if (title && /\/collections\/[^/]+\/?$/.test(path)) {
        storageSet('localStorage', LAST_COLL_KEY, JSON.stringify({ path: path, title: title.slice(0, 60), lang: lang, t: Date.now() }));
      }
      return;
    }
    var box = document.querySelector('[data-vt-continue]');
    if (!box) return;
    var data;
    try { data = JSON.parse(storageGet('localStorage', LAST_COLL_KEY) || 'null'); } catch (e) { data = null; }
    // a later visit only (not in the same visit she looked at it), and at most 60 days old
    if (!data || !data.path || Date.now() - (data.t || 0) > 60 * 864e5 || storageGet('sessionStorage', 'vt-coll-this-visit')) return;
    var link = box.querySelector('[data-vt-continue-link]');
    var t = box.querySelector('[data-vt-continue-title]');
    // open it in the language she is using now (/ar/collections/… ↔ /collections/…)
    var bare = data.path.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/collections\/)/i, '');
    link.href = root().replace(/\/$/, '') + bare;
    if (t) t.textContent = data.lang === lang ? data.title : '';
    box.hidden = false;
  }

  /* ---- U6: "Your bag is waiting" on a later visit (once per visit) ----------- */
  function initBagWaiting() {
    if (!cfg.bagWaiting) return;
    var LAST = 'vt-last-seen';
    var last = parseInt(storageGet('localStorage', LAST) || '0', 10);
    var now = Date.now();
    var newVisit = !storageGet('sessionStorage', 'vt-visit');
    storageSet('localStorage', LAST, String(now));
    if (!newVisit) return;
    storageSet('sessionStorage', 'vt-visit', '1');
    // a return visit = she was here before, at least 30 minutes ago
    if (!last || now - last < 30 * 60000) return;
    if (document.body.classList.contains('template-cart') || storageGet('sessionStorage', 'vt-bag-note')) return;
    fetch(route('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.item_count) return;
        storageSet('sessionStorage', 'vt-bag-note', '1');
        var main = document.getElementById('content');
        if (!main) return;
        var note = document.createElement('div');
        note.className = 'vt-bagnote';
        note.setAttribute('role', 'status');
        var text = cart.item_count === 1 ? S.bagWaitingOne : tpl(S.bagWaiting, { count: cart.item_count });
        note.innerHTML = '<div class="vt-bagnote__inner">' +
          '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 8h12l-1 12H7L6 8z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg>' +
          '<span class="vt-bagnote__text">' + esc(text) + '</span>' +
          '<a class="vt-bagnote__view" href="' + esc(route('cart')) + '" data-vt-bagnote-view>' + esc(S.bagWaitingView) + '</a>' +
          '<button type="button" class="vt-bagnote__close" data-vt-bagnote-close aria-label="' + esc(S.close) + '">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>';
        // the see-through menu bar (data-vt-header-overlay pages) covers the top of <main>: put the note above the menu bar
        var hdr = document.querySelector('.section-header');
        if (hdr && hdr.parentNode) hdr.parentNode.insertBefore(note, hdr);
        else main.insertBefore(note, main.firstChild);
        note.addEventListener('click', function (evt) {
          if (evt.target.closest('[data-vt-bagnote-close]')) { note.remove(); return; }
          var view = evt.target.closest('[data-vt-bagnote-view]');
          if (view && cfg.cartType === 'drawer' && document.querySelector('.js-cart-drawer')) {
            evt.preventDefault();
            note.remove();
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: view } }));
          }
        });
      })
      .catch(function () {});
  }

  /* ---- U5: "Undo" after removing an item from the bag (8 seconds) ------------- */
  var lastCart = null;
  function initCartUndo() {
    if (!cfg.cartUndo) return;
    var toast = null;
    var timer = null;
    var hide = function () {
      window.clearTimeout(timer);
      if (toast) { toast.remove(); toast = null; }
    };
    var show = function (line) {
      hide();
      toast = document.createElement('div');
      toast.className = 'vt-undo';
      toast.setAttribute('role', 'status');
      toast.innerHTML = '<span class="vt-undo__text">' + esc(tpl(S.undoRemoved, { title: line.title })) + '</span>' +
        '<button type="button" class="vt-undo__btn">' + esc(S.undoButton) + '</button>';
      document.body.appendChild(toast);
      var btn = toast.querySelector('.vt-undo__btn');
      btn.addEventListener('click', function () {
        btn.disabled = true;
        var item = { id: line.id, quantity: line.qty };
        if (line.properties && Object.keys(line.properties).length) item.properties = line.properties;
        fetch(route('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] })
        })
          .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
          .then(function (res) {
            if (!res.ok) throw new Error(friendlyError((res.data && (res.data.description || res.data.message)) || S.undoFailed));
            hide();
            announce(tpl(S.undoDone, { title: line.title }));
            document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          })
          .catch(function (err) {
            if (!toast) return;
            toast.querySelector('.vt-undo__text').textContent = err.message || S.undoFailed;
            btn.hidden = true;
            window.clearTimeout(timer);
            timer = window.setTimeout(hide, 5000);
          });
      });
      timer = window.setTimeout(hide, 8000);
    };
    var capture = function (item) {
      var input = item && item.querySelector('.cart-item__quantity-input');
      if (!input || !input.dataset.key) return null;
      var id = parseInt(String(input.dataset.key).split(':')[0], 10);
      if (!id) return null;
      var qty = parseInt(input.dataset.initialValue || input.value, 10) || 1;
      var titleEl = item.querySelector('.cart-item__title a, .cart-item__title');
      var line = { id: id, qty: qty, title: titleEl ? titleEl.textContent.trim() : '' };
      // keep line properties (none are used today) when the cart data is at hand
      if (lastCart && lastCart.items) {
        lastCart.items.forEach(function (it) { if (it.key === input.dataset.key && it.properties) line.properties = it.properties; });
      }
      return line;
    };
    document.addEventListener('click', function (evt) {
      var remove = evt.target.closest && evt.target.closest('.cart-item__remove');
      var down = !remove && evt.target.closest && evt.target.closest('.quantity-down');
      var trigger = remove || down;
      var item = trigger && trigger.closest('.cart-item');
      if (!item) return;
      if (down) {
        var input = item.querySelector('.cart-item__quantity-input');
        if (!input || parseInt(input.value, 10) > 1) return;
      }
      var line = capture(item);
      if (!line) return;
      // shown once the bag has really changed
      var done = function () {
        document.removeEventListener('on:cart:change', done);
        show(line);
      };
      document.addEventListener('on:cart:change', done);
      window.setTimeout(function () { document.removeEventListener('on:cart:change', done); }, 6000);
    }, true);
  }

  /* back to the same place on a collection: moved to vt-collection.js (round 9 T3) */

  /* ======================================================================
     Round 8 — footer, homepage hero, marquee, animations
     All motion is CSS; the script only adds classes (IntersectionObserver, no scroll listeners).
     Nothing is hidden unless this script runs, and only things still below the screen.
     ====================================================================== */
  function animationsOn() {
    return document.documentElement.classList.contains('vt-anim') && !reducedMotion() && 'IntersectionObserver' in window;
  }
  function belowScreen(el) {
    var r = el.getBoundingClientRect();
    return r.top > (window.innerHeight || document.documentElement.clientHeight) - 4;
  }
  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
    var target = document.querySelector('.logo__link') || document.querySelector('a[href]');
    if (target) window.setTimeout(function () { target.focus({ preventScroll: true }); }, reducedMotion() ? 0 : 400);
  }

  /* ---- R8 footer: accordions on phones, back to top, footer name ----------- */
  function initFooter() {
    var foot = document.querySelector('[data-vt-footer]');
    // back-to-top links in the footer (work without JS too: href="#")
    document.querySelectorAll('[data-vt-to-top]').forEach(function (a) {
      a.addEventListener('click', function (e) { e.preventDefault(); scrollToTop(); });
    });
    if (!foot) return;
    var wide = window.matchMedia('(min-width: 768px)');
    var accs = [].slice.call(foot.querySelectorAll('[data-vt-foot-acc]'));
    var apply = function () {
      accs.forEach(function (d) {
        var s = d.querySelector('summary');
        if (wide.matches) {
          d.open = true;
          if (s) s.setAttribute('tabindex', '-1');
        } else {
          d.open = false;
          if (s) s.removeAttribute('tabindex');
        }
      });
    };
    accs.forEach(function (d) {
      var s = d.querySelector('summary');
      if (s) s.addEventListener('click', function (e) { if (wide.matches) e.preventDefault(); });
    });
    apply();
    if (wide.addEventListener) wide.addEventListener('change', apply);
    else if (wide.addListener) wide.addListener(apply);

    if (!('IntersectionObserver' in window)) return;
    // the floating back-to-top button steps aside while the footer is on screen
    new IntersectionObserver(function (entries) {
      document.body.classList.toggle('vt-foot-in-view', entries[0].isIntersecting);
    }).observe(foot);
    // footer name slides in once
    var mark = foot.querySelector('.vt-foot__wordmark');
    if (mark && animationsOn() && belowScreen(mark)) {
      foot.classList.add('vt-foot--ready');
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        foot.classList.add('vt-foot--in');
        io.disconnect();
      }, { rootMargin: '0px 0px -5% 0px' });
      io.observe(mark);
    }
  }

  /* ---- R8 sections fade + rise on first view -------------------------------- */
  function initReveal() {
    if (!animationsOn()) return;
    var sections = [].slice.call(document.querySelectorAll('#content > .container > .shopify-section'));
    var todo = sections.filter(function (s, i) {
      if (i === 0) return false;
      if (s.querySelector('form[action*="/cart/add"], .product-form, [data-vt-no-reveal]')) return false;
      return belowScreen(s);
    });
    if (!todo.length) return;
    // r9 (T4): smoother on slow phones — sections that arrive together fade in one after another (80 ms apart, at
    // most 4 animate at once); any more, or one already scrolled past (fast fling / jump), just appear.
    var done = function (el, delay) {
      if (delay) el.style.transitionDelay = delay + 'ms';
      el.classList.add('vt-rv--in');
      window.setTimeout(function () { el.classList.remove('vt-rv', 'vt-rv--in'); el.style.transitionDelay = ''; }, 600 + (delay || 0));
    };
    var now = function (el) { el.classList.remove('vt-rv', 'vt-rv--in'); };
    var io = new IntersectionObserver(function (entries) {
      var n = 0;
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        if (n >= 4 || en.boundingClientRect.bottom < 0) { now(en.target); return; }
        done(en.target, n * 80);
        n += 1;
      });
    }, { rootMargin: '0px 0px -6% 0px' });
    todo.forEach(function (s) { s.classList.add('vt-rv'); io.observe(s); });
    // never leave anything hidden: printing, or a jump to an anchor inside a hidden section
    var showAll = function () { todo.forEach(function (s) { if (s.classList.contains('vt-rv')) { io.unobserve(s); done(s); } }); };
    window.addEventListener('beforeprint', showAll);
    window.addEventListener('hashchange', showAll);
  }

  /* ---- R8 numbers that count up once (final numbers are in the HTML) ------- */
  function initCountUp() {
    var nums = [].slice.call(document.querySelectorAll('[data-vt-count]'));
    if (!nums.length || !animationsOn()) return;
    var run = function (el) {
      var end = parseInt(el.getAttribute('data-vt-count'), 10) || 0;
      var suffix = el.getAttribute('data-vt-suffix') || '';
      var t0 = null;
      var dur = 400;
      var step = function (t) {
        if (t0 === null) t0 = t;
        var p = Math.min(1, (t - t0) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(end * eased) + suffix;
        if (p < 1) window.requestAnimationFrame(step);
        else el.textContent = end + suffix;
      };
      window.requestAnimationFrame(step);
    };
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        run(en.target);
      });
    }, { threshold: 0.6 });
    nums.forEach(function (el) {
      if (!belowScreen(el)) return; // already on screen: leave the number as it is
      var final = el.textContent;
      el.style.minWidth = final.length + 'ch';
      el.textContent = '0' + (el.getAttribute('data-vt-suffix') || '');
      io.observe(el);
    });
  }

  /* ---- R8 marquee: pause button + stop while touched ------------------------- */
  function initMarquee() {
    document.querySelectorAll('[data-vt-mq-toggle]').forEach(function (btn) {
      var host = btn.closest('scrolling-banner') || btn.parentNode;
      var box = btn.closest('.marquee-container');
      var label = btn.querySelector('[data-vt-mq-label]');
      btn.hidden = false;
      btn.addEventListener('click', function () {
        var paused = btn.getAttribute('aria-pressed') !== 'true';
        btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
        host.classList.toggle('vt-mq-paused', paused);
        if (label) label.textContent = btn.getAttribute(paused ? 'data-label-play' : 'data-label-pause') || '';
      });
      if (!box) return;
      var timer = null;
      box.addEventListener('touchstart', function () { window.clearTimeout(timer); box.classList.add('vt-mq-touch'); }, { passive: true });
      var release = function () { window.clearTimeout(timer); timer = window.setTimeout(function () { box.classList.remove('vt-mq-touch'); }, 1500); };
      box.addEventListener('touchend', release, { passive: true });
      box.addEventListener('touchcancel', release, { passive: true });
    });
  }

  /* ---- R8 hero video: pause button, no video on 2G ----------------------------- */
  function initHeroVideo() {
    document.querySelectorAll('[data-vt-hero]').forEach(function (hero) {
      var vv = hero.querySelector('vt-hero-video');
      var btn = hero.querySelector('[data-vt-hero-pause]');
      if (!vv) return;
      var conn = navigator.connection;
      if (conn && /(^|-)2g$/.test(conn.effectiveType || '')) {
        // the video snippet starts after "load" and reads these: empty = keep the photo
        vv.setAttribute('data-desktop-src', '');
        vv.setAttribute('data-mobile-src', '');
        return;
      }
      var video = vv.querySelector('video');
      if (!btn || !video) return;
      var label = btn.querySelector('[data-vt-hero-pause-label]');
      var userPaused = false;
      var sync = function () { btn.hidden = !vv.classList.contains('is-playing') && !userPaused; };
      if ('MutationObserver' in window) new MutationObserver(sync).observe(vv, { attributes: true, attributeFilter: ['class'] });
      video.addEventListener('play', function () { if (userPaused) video.pause(); });
      btn.addEventListener('click', function () {
        userPaused = !userPaused;
        btn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        if (label) label.textContent = btn.getAttribute(userPaused ? 'data-label-play' : 'data-label-pause') || '';
        if (userPaused) video.pause();
        else { var p = video.play(); if (p && p.catch) p.catch(function () {}); }
      });
      sync();
    });
  }

  /* ==================================================================== */
  /* Round 9 — header, menus, search (D3, U6, U8, T5)                      */
  /* ==================================================================== */

  /* ---- D3: slimmer sticky header after she scrolls down ------------------------------------
     Only toggles classes on <html>; the look is in verona.css ("Round 9 — header"). Transform only, so nothing in
     the page moves (no layout shift). Hysteresis (small after 140px, full again above 60px) so it never flickers.
     Not changed while the search panel or the phone menu is open. */
  function initHeaderShrink() {
    if (!cfg.headerShrink) return;
    var sec = document.querySelector('.section-header');
    if (!sec) return;
    var pos = window.getComputedStyle(sec).position;
    if (pos !== 'sticky' && pos !== '-webkit-sticky' && pos !== 'fixed') return;
    var html = document.documentElement;
    var bar = document.querySelector('.section-announcement-bar');
    var base = bar ? bar.offsetHeight : 0;
    var small = false;
    var ticking = false;
    html.classList.add('vt-hdr-js');
    var update = function () {
      ticking = false;
      var b = document.body;
      if (b.classList.contains('show-search') || b.classList.contains('reveal-mobile-nav')) return false;
      var y = window.scrollY || window.pageYOffset || 0;
      var want = small ? y > base + 60 : y > base + 140;
      if (want === small) return false;
      small = want;
      html.classList.toggle('vt-hdr-small', small);
      return true;
    };
    // r12: one scroll handler for the whole header (sections/header.liquid) when it is there
    if (window.vtHdrScroll && window.vtHdrScroll.add) { window.vtHdrScroll.add(update); return; }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  /* ---- U6: recently viewed in the phone menu --------------------------------------------------
     Filled the first time the menu opens (the menu is built by Symmetry from a template, so we look for it then).
     Up to 6 products from her own device (same list as the "Recently viewed" rows), each a photo, name and price. */
  function initMenuRecent() {
    if (!cfg.menuRecent || !cfg.recentlyViewed) return;
    var filled = false;
    var fill = function () {
      if (filled) return;
      var box = document.querySelector('.mobile-navigation-drawer [data-vt-menu-recent]');
      if (!box) return;
      filled = true;
      var list = box.querySelector('[data-vt-menu-recent-list]');
      var current = cfg.productHandle;
      var handles = readList(RECENT_KEY).filter(function (h) { return h !== current; }).slice(0, 6);
      if (!handles.length || !list) return;
      Promise.all(handles.map(fetchProduct)).then(function (products) {
        var html = products.filter(Boolean).map(function (p) {
          var img = p.featured_image ? imageUrl(p.featured_image, 220) : '';
          return '<li class="vt-menu-recent__item"><a class="vt-menu-recent__link" href="' + esc(route('products/' + p.handle)) + '">' +
            (img ? '<img class="vt-menu-recent__img" src="' + esc(img) + '" alt="" width="104" height="130" loading="lazy">' : '<span class="vt-menu-recent__img" aria-hidden="true"></span>') +
            '<span class="vt-menu-recent__name">' + esc(p.title) + '</span>' +
            '<span class="vt-menu-recent__price">' + esc(formatMoney(p.price)) + '</span></a></li>';
        }).join('');
        if (!html) return;
        list.innerHTML = html;
        box.hidden = false;
      });
    };
    // the menu opens with a click on any .mobile-nav-toggle (also the bottom bar's "Shop") — fill it right after
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.mobile-nav-toggle, [data-vt-bnav-action="menu"]')) window.setTimeout(fill, 30);
    });
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        if (document.body.classList.contains('reveal-mobile-nav')) fill();
      }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
  }

  /* ---- U8: recent searches (her last 6, on her device only) ---------------------------------
     Saved when she submits any search form (what she typed, before Arabic/typo fixing) or lands on a search results
     page. Shown as chips in the header search panel and on the search page (snippets/vt-recent-searches.liquid);
     "Clear" empties the list and says so to screen readers. */
  var RSEARCH_KEY = 'vt-recent-searches';
  var RSEARCH_MAX = 6;
  function rememberSearch(q) {
    q = String(q || '').replace(/\s+/g, ' ').trim().slice(0, 60);
    if (q.length < 2) return;
    var lower = q.toLowerCase();
    var list = readList(RSEARCH_KEY).filter(function (x) { return x.toLowerCase() !== lower; });
    list.unshift(q);
    writeList(RSEARCH_KEY, list.slice(0, RSEARCH_MAX));
    drawRecentSearches();
  }
  function drawRecentSearches() {
    var list = readList(RSEARCH_KEY).slice(0, RSEARCH_MAX);
    var icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M12 7v5l3 2"/><circle cx="12" cy="12" r="8.5"/></svg>';
    document.querySelectorAll('[data-vt-rsearch]').forEach(function (box) {
      var ul = box.querySelector('[data-vt-rsearch-list]');
      if (!ul) return;
      if (!list.length) { ul.innerHTML = ''; box.hidden = true; return; }
      ul.innerHTML = list.map(function (q) {
        var fixed = normaliseSearch(q);
        var href = route('search') + '?q=' + encodeURIComponent(fixed) + (fixed !== q ? '&vt_orig=' + encodeURIComponent(q) : '');
        return '<li><a class="vt-rsearch__chip" href="' + esc(href) + '">' + icon + '<span>' + esc(q) + '</span></a></li>';
      }).join('');
      box.hidden = false;
    });
  }
  function initRecentSearches() {
    if (!cfg.recentSearches) return;
    // landing on a results page counts too (what she typed, if the words were fixed: vt_orig)
    if (/\/search\/?$/.test(window.location.pathname)) {
      var params = new URLSearchParams(window.location.search);
      var q = params.get('vt_orig') || params.get('q');
      if (q) rememberSearch(q);
    }
    document.addEventListener('submit', function (evt) {
      var form = evt.target;
      if (!isSearchForm(form)) return;
      var input = form.querySelector('input[name="q"]');
      var orig = form.querySelector('input[name="vt_orig"]');
      if (input) rememberSearch(orig ? orig.value : input.value);
    }, true);
    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('[data-vt-rsearch-clear]');
      if (!btn) return;
      evt.preventDefault();
      writeList(RSEARCH_KEY, []);
      drawRecentSearches();
      var input = btn.closest('.main-search, .vt-sfield, form, body').querySelector('input[name="q"]');
      if (input) input.focus();
      announce(S.recentCleared);
    });
    drawRecentSearches();
  }

  /* ---- T5: the header search panel tells screen readers how many products were found -------- */
  function initSearchStatus() {
    var panel = document.querySelector('.section-header main-search');
    var status = panel && panel.querySelector('[data-vt-search-status]');
    if (!panel || !status || !('MutationObserver' in window)) return;
    var last = '';
    var timer = null;
    new MutationObserver(function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        var msg = '';
        if (panel.classList.contains('main-search--has-results')) {
          msg = tpl(S.searchFound, { count: panel.querySelectorAll('.main-search__results .main-search-result.product-block, .main-search__results .product-block.main-search-result').length });
        } else if (panel.classList.contains('main-search--no-results')) {
          msg = S.searchNone || '';
        }
        if (msg && msg !== last) { last = msg; status.textContent = msg; }
        if (!msg) last = '';
      }, 400);
    }).observe(panel, { attributes: true, attributeFilter: ['class'] });
  }

  window.vtTheme = {
    route: route, esc: esc, tpl: tpl, announce: announce, formatMoney: formatMoney, cardHtml: cardHtml,
    skeletonCards: skeletonCards, fetchProduct: fetchProduct, friendlyError: friendlyError, strings: S, config: cfg
  };

  /* round 9 (T3): shared helpers for the page files (vt-product.js, vt-collection.js) and later features.
     Documented at the top of this file. Set before the page files run (they are deferred and come after). */
  window.vtCore = {
    // config + translated strings (snippets/vt-config.liquid)
    cfg: cfg, S: S,
    // storage keys shared between files
    keys: { wishlist: WISHLIST_KEY, recent: RECENT_KEY, recentMax: RECENT_MAX, sizeMemory: SIZE_KEY },
    // urls, text, money
    root: root, route: route, tpl: tpl, esc: esc, formatMoney: formatMoney, shortMoney: shortMoney,
    // storage
    readList: readList, writeList: writeList, storageGet: storageGet, storageSet: storageSet,
    readSizeMemory: readSizeMemory, writeSizeMemory: writeSizeMemory,
    // start-up + page helpers
    onReady: onReady, parseJsonEl: parseJsonEl, jsonFetch: jsonFetch, loadScriptOnce: loadScriptOnce,
    reducedMotion: reducedMotion, animationsOn: animationsOn, belowScreen: belowScreen, scrollToTop: scrollToTop,
    // products + cards
    isSizeOption: isSizeOption, colourOption: colourOption, imageUrl: imageUrl, fetchProduct: fetchProduct,
    skeletonCards: skeletonCards, cardHtml: cardHtml, normaliseCollectionProduct: normaliseCollectionProduct,
    // cart
    onCart: onCart, refreshCart: refreshCart, openCartAfterAdd: openCartAfterAdd, friendlyError: friendlyError,
    // screen readers
    announce: announce,
    // hooks: collection / search listing re-rendered by Symmetry (filters, sort)
    onListingRefresh: onListingRefresh,
    // search words (Arabic + typos) and the wishlist store
    normaliseSearch: normaliseSearch, wishlist: wishlist
  };
  /* ---- "Order Now - Cash on Delivery" (EasySell app) looks like the theme's own buttons --------------------
     The app writes its size, weight and corners inline with !important, which no stylesheet can beat; setting the
     same properties again from here replaces them. Its button arrives after the page, so watch for it. The colour
     is the app's own setting (olive). Styles for the rest: assets/vt-r14.css "5". Product pages and the bag page. */
  /* ---- language: first visit follows the device, a chosen language is remembered (Sep 2026) ------------------------
     If the visitor has never picked a language, the first page they open is shown in their device's language when the
     site has it (Arabic phones and computers get /ar, others get English). Tapping EN / عربي anywhere (navbar, top bar,
     phone menu, footer) is remembered: from then on every page opens in that language. Search engines and link previews
     are never redirected. */
  function initAutoLanguage() {
    var CHOICE = 'vt-lang-choice', DONE = 'vt-lang-auto';
    var store = function (k, v) { try { if (v === undefined) return window.localStorage.getItem(k); window.localStorage.setItem(k, v); } catch (e) { return null; } };
    document.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('.vt-lang__btn, .vt-lang-nav__btn, button[name="language_code"], [name="locale_code"]');
      if (b) store(CHOICE, String(b.getAttribute('data-vt-lang-choice') || b.value || b.lang || '').slice(0, 2).toLowerCase());
    }, true);
    if (navigator.webdriver || /bot|crawl|spider|slurp|preview|facebookexternalhit|whatsapp|telegram/i.test(navigator.userAgent || '')) return;
    var html = document.documentElement, onAr = /^ar/i.test(html.lang || '');
    var hasAr = !!document.querySelector('.vt-lang__btn[lang^="ar"], .vt-lang-nav__btn[lang^="ar"], link[hreflang^="ar"]') || onAr;
    if (!hasAr) return;
    var wantAr;
    var chosen = store(CHOICE);
    if (chosen === 'ar' || chosen === 'en') {
      wantAr = chosen === 'ar';                 // a language she picked: always shown in it
    } else {
      if (store(DONE)) return;                  // the device language is only followed on the very first visit
      store(DONE, '1');
      var langs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || '']).map(function (l) { return String(l).toLowerCase(); });
      wantAr = /^ar/.test(langs[0] || '');
    }
    if (wantAr === onAr) return;
    var path = window.location.pathname.replace(/^\/ar(?=\/|$)/, '') || '/';
    var target = (wantAr ? '/ar' + (path === '/' ? '' : path) : path) + window.location.search + window.location.hash;
    window.location.replace(target);
  }

  function initOrderNowLook() {
    // the app's button is on product pages and the bag page only
    if (!/\/products\/|\/cart/.test(window.location.pathname)) return;
    var LOOK = [['font-size', '13px'], ['font-weight', '600'], ['border-radius', '0px'], ['font-family', 'inherit']];
    var fix = function (el) {
      if (!el || !el.style) return;
      LOOK.forEach(function (kv) { if (el.style.getPropertyValue(kv[0]) !== kv[1] || el.style.getPropertyPriority(kv[0]) !== 'important') el.style.setProperty(kv[0], kv[1], 'important'); });
      el.classList.add('vt-es-btn');
    };
    var scan = function () { [].forEach.call(document.querySelectorAll('.es-popup-button, #es-form .es-cta-btn'), fix); };
    scan();
    var queued = false;
    new MutationObserver(function () {
      if (queued) return; queued = true;
      window.setTimeout(function () { queued = false; scan(); }, 120);
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
  }


  /* ------------------------------------------------------------------ boot */
  onReady(function () {
    initWishlist();
    initLowStock();
    initPoints();
    initBottomNav();
    initSizeGuide();
    initSaveLine();
    initCardSizes();
    initMotion();
    initSearch();
    initFriendlyErrors();
    initCardTaps();
    initCompare();
    initBackToTop();
    initPrefetch();
    initConverterLink();
    // round 7
    initColourCaption();
    initStockLine();
    initColourSeason();
    initContinueChip();
    initBagWaiting();
    initCartUndo();
    // round 8
    initFooter();
    initReveal();
    initCountUp();
    initMarquee();
    initHeroVideo();
    // round 9
    initHeaderShrink();
    initMenuRecent();
    initRecentSearches();
    initSearchStatus();
    initOrderNowLook();
    initAutoLanguage();
    onCart(function (cart) { lastCart = cart; });
    ['on:cart:add', 'on:cart:change', 'on:cart:after-merge'].forEach(function (name) {
      document.addEventListener(name, refreshCart);
    });
    window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCart(); });
  });
})();

/* ---------------------------------------------------------------------------
   2026-09-24 — Verona Rewards opener. Any [data-vt-rewards-open] link (bottom bar "Rewards", the account page
   "See my points", the rewards band) opens the Kangaroo Rewards panel, where a signed-in customer sees her
   points balance. If the panel has not loaded (or the app is off), the link simply opens its href.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.vtRewardsOpener) return;
  window.vtRewardsOpener = true;
  function launcher() { return document.getElementById('kr-widget-launcher-button'); }
  window.vtOpenRewards = function () {
    var l = launcher();
    if (!l) return false;
    l.click();
    return true;
  };
  document.addEventListener('click', function (evt) {
    var a = evt.target.closest && evt.target.closest('[data-vt-rewards-open]');
    if (!a) return;
    if (window.vtOpenRewards()) { evt.preventDefault(); return; }
    // The panel file loads a little after the page (see vt-kangaroo-tweaks). Fetch it now, wait up to 8 s
    // for the panel, and fall back to the Rewards page if it never shows.
    if (!window.vtKgrRelease || a.getAttribute('aria-busy') === 'true') return;
    evt.preventDefault();
    window.vtKgrRelease();
    a.setAttribute('aria-busy', 'true');
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      var opened = window.vtOpenRewards();
      if (opened || tries > 53) {
        clearInterval(timer);
        a.removeAttribute('aria-busy');
        if (!opened && a.href) window.location.href = a.href;
      }
    }, 150);
  });
})();

/* ---------------------------------------------------------------------------
   25 Sep 2026 — at the very bottom of the page the floating buttons (Kangaroo "Rewards" pill on computers, chat
   bubble) sat on top of the footer's last row (payment / privacy links). While that row is on screen they fade away
   (html.vt-at-foot, see assets/verona.css) and come back as soon as she scrolls up.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.vtFootLift) return;
  window.vtFootLift = true;
  function init() {
    var rows = document.querySelectorAll('.vt-foot__bottom');
    var row = rows[rows.length - 1];
    if (!row) return;
    var root = document.documentElement;
    var ticking = false;
    function update() {
      ticking = false;
      var r = row.getBoundingClientRect();
      var seen = Math.max(0, Math.min(r.height, window.innerHeight - r.top));
      root.classList.toggle('vt-at-foot', seen > 24);
    }
    function ask() { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }
    window.addEventListener('scroll', ask, { passive: true });
    window.addEventListener('resize', ask);
    update();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ---------------------------------------------------------------------------
   25 Sep 2026 — phones hide Shopify's floating "Protected by hCaptcha" box (assets/verona.css). hCaptcha asks for its
   notice to stay visible when the box is hidden, so a small line is added under every form that gets the captcha.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.vtHcNote) return;
  window.vtHcNote = true;
  var ar = (document.documentElement.lang || '').indexOf('ar') === 0;
  var html = ar
    ? 'هذا النموذج محمي بواسطة hCaptcha وتنطبق <a href="https://www.hcaptcha.com/privacy" target="_blank" rel="noopener">سياسة الخصوصية</a> و<a href="https://www.hcaptcha.com/terms" target="_blank" rel="noopener">الشروط</a>.'
    : 'Protected by hCaptcha — its <a href="https://www.hcaptcha.com/privacy" target="_blank" rel="noopener">Privacy Policy</a> and <a href="https://www.hcaptcha.com/terms" target="_blank" rel="noopener">Terms</a> apply.';
  function add(box) {
    var form = box.closest('form');
    if (!form || form.querySelector('.vt-hc-note')) return;
    var p = document.createElement('p');
    p.className = 'vt-hc-note';
    p.innerHTML = html;
    form.appendChild(p);
  }
  function scan() { document.querySelectorAll('form .h-captcha').forEach(add); }
  if (!('MutationObserver' in window)) return;
  // only look again when a captcha box was actually added (the page changes a lot; keep this cheap)
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i += 1) {
      var nodes = records[i].addedNodes;
      for (var j = 0; j < nodes.length; j += 1) {
        var n = nodes[j];
        if (n.nodeType === 1 && (n.classList.contains('h-captcha') || (n.querySelector && n.querySelector('.h-captcha')))) { scan(); return; }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
  scan();
})();

/* Verona Tempo (Sep 2026, user: "when I press it, it doesn't send me to the website with me logged in"):
   every sign-in link (header icon's "Sign in to my account", rewards page, Kangaroo panel's Login / Register …)
   went to /customer_authentication/redirect, which lands on Shopify's account page (Orders / Profile) and never
   signs her in on the website. /customer_authentication/login?return_to=<this page> signs her in on the website
   itself and brings her back to the page she was on (the draft-theme preview stays on too). Links are rewritten
   when clicked, so links other apps add later (the rewards panel) are covered. Signed-in pages are left alone. */
(function () {
  'use strict';
  if (window.vtLoginBack) return;
  window.vtLoginBack = true;
  var LOGIN = /\/(customer_authentication\/redirect|account\/login|account\/register)\/?$/;
  function target() {
    var back = location.pathname + location.search;
    if (/customer_authentication|\/account(\/|$)/.test(location.pathname)) back = '/';
    return '/customer_authentication/login?return_to=' + encodeURIComponent(back);
  }
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var path = e.composedPath ? e.composedPath() : [e.target];
    var a = null;
    for (var i = 0; i < path.length; i += 1) { if (path[i] && path[i].tagName === 'A' && path[i].href) { a = path[i]; break; } }
    if (!a || a.hasAttribute('data-vt-auth-open')) return; // the header icon still opens the sign-in overlay first
    var u;
    try { u = new URL(a.href, location.href); } catch (err) { return; }
    if (u.host !== location.host || !LOGIN.test(u.pathname)) return;
    e.preventDefault();
    if (a.target === '_blank') window.open(target(), '_blank', 'noopener');
    else location.href = target();
  }, true);
})();

/* Verona Tempo (Sep 2026, user: "change the Order Now button in Arabic too"): the EasySell cash-on-delivery button
   and its form only come in English. On the Arabic site the theme swaps their words for Arabic (texts and
   placeholders only — the fields, prices and what is sent stay exactly as the app makes them). */
(function () {
  'use strict';
  if (window.vtEsArabic) return;
  window.vtEsArabic = true;
  if ((document.documentElement.lang || '').indexOf('ar') !== 0) return;
  if (!/\/products\/|\/cart/.test(window.location.pathname)) return;
  var T = {
    'order now - cash on delivery': 'اطلبي الآن - الدفع عند الاستلام',
    'please fill in the form to order': 'يرجى تعبئة النموذج لإتمام الطلب',
    'subtotal': 'المجموع الفرعي', 'shipping': 'التوصيل', 'total': 'المجموع',
    'name': 'الاسم', 'phone': 'رقم الهاتف', 'address': 'العنوان', 'region': 'المنطقة', 'city': 'المدينة',
    'email': 'البريد الإلكتروني', 'notes': 'ملاحظات', 'note': 'ملاحظة',
    'discount code': 'كود الخصم', 'apply': 'تطبيق', 'free': 'مجاني',
    'subscribe to stay updated with new products and offers!': 'اشتركي لتصلك أحدث المنتجات والعروض!',
    '!subscribe to stay updated with new products and offers': 'اشتركي لتصلك أحدث المنتجات والعروض!',
    // 8 Oct 2026 (ads team): the regions and delivery choices in Arabic too (only the words shown; what is sent stays)
    'akkar': 'عكار', 'baalbek-hermel': 'بعلبك الهرمل', 'beirut': 'بيروت', 'beqaa': 'البقاع', 'mount lebanon': 'جبل لبنان',
    'nabatieh': 'النبطية', 'north': 'الشمال', 'south': 'الجنوب', 'keserwan-jbeil': 'كسروان جبيل',
    'shipping options': 'خيارات التوصيل', 'delivery all over lebanon': 'توصيل لكل لبنان', 'free shipping': 'توصيل مجاني',
    'free delivery (orders over $95)': 'توصيل مجاني (للطلبات فوق 95$)',
    'free delivery (orders over $70)': 'توصيل مجاني (للطلبات فوق 70$)'
  };
  var BUY = /^\s*buy it now\b/i;
  function swapText(node) {
    var raw = node.nodeValue, t = raw.trim();
    if (!t) return;
    var k = t.toLowerCase();
    if (T[k]) { node.nodeValue = raw.replace(t, T[k]); return; }
    if (BUY.test(t)) node.nodeValue = raw.replace(/buy it now/i, 'اطلبي الآن');
  }
  function inApp(el) { return el && el.closest && el.closest('#es-popup-button, .es-popup-button, #es-form, .es-form, [id^="es-popup"], [class*="es-popup"], [class*="es-form"]'); }
  function scan() {
    var roots = document.querySelectorAll('#es-popup-button, .es-popup-button, #es-form, .es-form, [id^="es-popup"], [class*="es-popup-"], [class*="es-form"]');
    [].forEach.call(roots, function (root) {
      var w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null), n;
      while ((n = w.nextNode())) swapText(n);
      [].forEach.call(root.querySelectorAll('input[placeholder], textarea[placeholder]'), function (i) {
        var k = i.getAttribute('placeholder').trim().toLowerCase();
        if (T[k]) i.setAttribute('placeholder', T[k]);
      });
    });
  }
  scan();
  var queued = false;
  new MutationObserver(function (records) {
    if (queued) return;
    for (var i = 0; i < records.length; i += 1) {
      if (inApp(records[i].target.nodeType === 1 ? records[i].target : records[i].target.parentElement) || [].some.call(records[i].addedNodes, function (a) { return a.nodeType === 1 && (inApp(a) || (a.querySelector && a.querySelector('[class*="es-"],[id^="es-"]'))); })) {
        queued = true;
        window.setTimeout(function () { queued = false; scan(); }, 80);
        return;
      }
    }
  }).observe(document.body, { childList: true, subtree: true, characterData: true });
})();

/* 8 Oct 2026 (speed): the moving text bands (marquee) are paused while they are off the screen — a running band made
   phones redraw the whole homepage every frame even after she had scrolled past it. Nothing changes on screen. */
(function () {
  if (!('IntersectionObserver' in window)) return;
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      Array.prototype.forEach.call(e.target.querySelectorAll('.marquee-content'), function (c) {
        c.style.animationPlayState = e.isIntersecting ? '' : 'paused';
      });
    });
  });
  function watch() {
    Array.prototype.forEach.call(document.querySelectorAll('.marquee'), function (m) {
      if (m.vtMqWatched) return;
      m.vtMqWatched = true;
      io.observe(m);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();
